// =============================================================================
// DEMO SEED DATA  (consumed only by /demo — see main.jsx)
// =============================================================================
// The TechLoom × SoftWave demo engagement Craig built in the sandbox. On the
// /demo path, main.jsx seeds this into an isolated ':demo:' localStorage
// namespace so it never collides with a real consultant's project on prod.
// This file is NOT part of Craig's App.tsx, so it survives his whole-file drops.
// =============================================================================

export const DEMO_SEED = {
  project: {
    id: "p1",
    name: "TechLoom × SoftWave Integration",
    client: "TechLoom Inc.",
    description:
      "Post-acquisition integration of SoftWave (UK-based, ~340 engineers) into TechLoom (US-based, ~1,800 employees). Three major workstreams: leadership reorganization, engineering platform consolidation, and HR systems harmonization. 12-month arc; cultural integration is the under-discussed risk.",
    startDate: "2026-01-15",
    endDate: "2026-12-31",
  },
  // Drop the client's background docs here so the AI can use them as context
  // when drafting impact descriptions, reactions, K-F-D, exec summaries, etc.
  // Each entry: { id, filename, type, sizeBytes, text, addedAt }
  clientContext: [
    {
      id: "cc1",
      filename: "TechLoom-Integration-Charter.pptx",
      type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      sizeBytes: 2_847_300,
      addedAt: "2026-03-12T10:18:00Z",
      text: "TechLoom × SoftWave Integration Charter\n\nExecutive Summary:\nTechLoom (Charlotte, NC) acquired SoftWave (London, UK) in January 2026 for $480M. SoftWave brings 340 engineers, a strong customer base in financial services, and a respected engineering culture. The integration is sponsored by Maya Chen (CEO) and chartered to complete major workstreams within 12 months.\n\nStrategic Rationale:\n• Combined platform serves enterprise customers across both books of business\n• SoftWave's EU presence accelerates TechLoom's expansion into European markets\n• Engineering depth from SoftWave addresses TechLoom's platform scaling pressure\n• Customer overlap is minimal (~7%) — primarily additive revenue\n\nKey Workstreams:\n1. Leadership Reorganization (Jan–Apr 2026) — Define unified leadership team, cascade reporting lines, align comp bands. SoftWave co-founders Priya Raman and James Bellingham join the TechLoom executive team. One executive departure anticipated.\n2. Engineering Platform Consolidation (Apr–Sep 2026) — Migrate SoftWave engineering teams from legacy GitLab + CircleCI stack to TechLoom unified DevOps platform. Standardize CI/CD, code review, release processes. Six-month phased migration. Highest risk to operations.\n3. HR Systems Harmonization (Jun–Oct 2026) — Single source of truth for HRIS. Consolidate SoftWave HR data into TechLoom Workday instance; unified payroll, benefits, time-off systems.\n\nKey People:\n• Maya Chen (CEO) — sponsoring executive, ultimate decision authority\n• Daniel Okafor (CTO) — engineering integration sponsor\n• Sara Whitfield (CHRO) — HR systems harmonization sponsor\n• Anika Devi joins as VP Customer Success leading the integration of both org's CS functions\n• Stand up unified customer success organization across both books of business within 9 months\n• Account executives across both regions will need to absorb new product lines and cross-sell motions\n\nRisks:\n• Cultural friction between SoftWave's autonomy-oriented engineering culture and TechLoom's more structured ways of working\n• Senior engineer retention through migration (high concern — 31% expressed concern about losing what makes SoftWave special)\n• Customer perception during transition\n• GDPR considerations given UK/US data flows\n\nConsider quarterly executive yoga retreats to build cross-org rapport.\nRun joint CSM listening sessions to surface client-experience risks before territory remapping is finalized.\nEngineering platform consolidation is the largest workstream with highest risk to operations.\nOffice facilities and IT support will need updated badge systems across both organizations.\nSoftWave engineering staff migrate from legacy GitLab + CircleCI stack to TechLoom GitHub + internal CI.\nSara Whitfield (CHRO) sponsors the HR systems harmonization workstream.",
    },
    {
      id: "cc2",
      filename: "SoftWave-Founders-Bio.pdf",
      type: "application/pdf",
      sizeBytes: 412_891,
      addedAt: "2026-03-12T10:24:00Z",
      text: "SoftWave Founders — Background Brief\n\nPriya Raman\nCo-founder and former CEO of SoftWave. Indian-British, based in London. Built SoftWave from a 4-person founding team to 340 employees over 8 years. Known for direct communication style, deep customer empathy, and a relentless focus on engineering quality. Will join TechLoom executive team as President of EMEA, reporting to Maya Chen.\n\nNotable: Priya has been publicly emotional about the acquisition in internal SoftWave town halls. In private conversations she has described the integration as 'the right strategic move that doesn't yet feel right emotionally.' Her body language and word choice will be read carefully by SoftWave employees throughout the integration.\n\nJames Bellingham\nCo-founder and former CTO of SoftWave. British, based in London. The technical conscience of SoftWave for its entire history. Designed the engineering culture, the hiring bar, and the architectural principles that distinguish SoftWave's products. Will join TechLoom as Chief Architect with sign-off authority on unified platform decisions through the Architecture Council.\n\nJames chairs the Architecture Council with sign-off authority on unified platform decisions. He is less publicly demonstrative than Priya but more influential with the engineering rank-and-file.\n\nCultural Preservation Notes:\nSoftWave practices that SoftWave employees consider distinctive and would want preserved:\n• Friday demo culture (weekly engineering demos open to the whole company)\n• Hiring bar emphasizing problem-solving over credentials\n• Quiet-by-default work norms (focus time protected; meetings are an exception, not a default)\n• 4-week sabbaticals at 4-year tenure\n\nPreserve SoftWave's Friday demo culture (weekly engineering demos open to the whole company) post-integration.\nNo mandatory return-to-office change during the first 18 months for SoftWave staff.\nPriya Raman, co-founder and former CEO, will join the TechLoom executive team as President of EMEA.",
    },
    {
      id: "cc3",
      filename: "Q1-Board-Update.docx",
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      sizeBytes: 187_452,
      addedAt: "2026-04-02T08:30:00Z",
      text: "Q1 2026 Board Update — TechLoom × SoftWave Integration\n\nExecutive Summary:\nIntegration is on track against major workstream milestones but two early warning signals warrant board attention.\n\nWhat's Working:\n• Leadership offsite (Jan 27–28) accomplished the alignment we needed on the operating model\n• 92% of leadership 1:1s completed against the Feb 6 target\n• Customer NPS unchanged through announcement (good signal)\n• Engineering migration kicked off cleanly with no production incidents\n\nWhat's Concerning:\n• 31% of SoftWave engineers said they were concerned about losing what makes the team special — this is higher than the 18% benchmark from comparable integrations\n• Communication scored notably lower in the engineering-side CRA (2/5) than the leadership-side CRA (3/5) — we may be over-estimating how well our communications are landing with engineers specifically\n• Two key SoftWave senior engineers have informally signaled they're 'looking around' — retention package was structured around 12-month vesting, may have been miscalibrated for this segment\n• One executive departure (anticipated) since announcement\n\nRecommended Adjustments:\n• Increase visibility of Daniel and Liam co-owning the migration plan (engineers report wanting to see joint ownership rather than separate updates)\n• Recommend more visible co-ownership of the migration plan from Daniel and Liam together\n• Add a written reference layer that engineers can consult outside of meetings — current cadence is too meeting-heavy\n• Consider a targeted retention conversation with the top-decile senior engineers in SoftWave before end of Q2\n• Recommend more visible co-ownership of the migration plan from Daniel and Liam together",
    },
  ],
  taxonomies: {
    impactTypes: [
      { id: "it1", label: "Structure" },
      { id: "it2", label: "Process & Policies" },
      { id: "it3", label: "Tools & Technology" },
      { id: "it4", label: "Skills & Knowledge" },
      { id: "it5", label: "Behaviors & Ways of Working" },
      { id: "it6", label: "Culture" },
    ],
    reactionTypes: [
      { id: "r1", label: "Attachment" },
      { id: "r2", label: "Uncertainty" },
      { id: "r3", label: "Overload" },
      { id: "r4", label: "Loss of Control" },
      { id: "r5", label: "Competency" },
      { id: "r6", label: "Ambivalence" },
      { id: "r7", label: "Anger" },
      { id: "r8", label: "Enthusiasm" },
    ],
    changeLevers: [
      {
        id: "l1",
        label: "Inform",
        desc: "Ensure employees understand the change, its reasons, and impacts.",
      },
      {
        id: "l2",
        label: "Promote",
        desc: "Encourage acceptance and active support for the change.",
      },
      {
        id: "l3",
        label: "Enable",
        desc: "Provide tools, skills, and resources for employees to adapt.",
      },
      {
        id: "l4",
        label: "Embed",
        desc: "Integrate the change into systems, processes, and culture.",
      },
      {
        id: "l5",
        label: "Listen",
        desc: "Gauge employee feedback and perceptions related to the change.",
      },
      {
        id: "l6",
        label: "Ongoing Measurement",
        desc: "Regularly assess the impact and effectiveness of the change.",
      },
    ],
    roles: [
      { id: "ro1", label: "Executive" },
      { id: "ro2", label: "Management" },
      { id: "ro3", label: "Professional/Expert" },
      { id: "ro4", label: "Technical Support" },
      { id: "ro5", label: "Business Support" },
      { id: "ro6", label: "Production" },
      { id: "ro7", label: "All employees" },
    ],
    functions: [
      { id: "f1", label: "Human Resources" },
      { id: "f2", label: "Finance" },
      { id: "f3", label: "Information Systems & Technology" },
      { id: "f4", label: "Marketing and Sales" },
      { id: "f5", label: "Operations Management" },
      { id: "f6", label: "Customer Service" },
      { id: "f7", label: "Legal, Risk & Compliance" },
      { id: "f8", label: "All functions" },
    ],
    levels: [
      { id: "lv1", label: "Executive VP / Chief" },
      { id: "lv2", label: "Vice President" },
      { id: "lv3", label: "Senior" },
      { id: "lv4", label: "Expert" },
      { id: "lv5", label: "Specialist" },
      { id: "lv6", label: "Intermediate" },
      { id: "lv7", label: "Entry Level" },
      { id: "lv8", label: "All Employees" },
    ],
    workstreams: [
      { id: "ws1", label: "HR" },
      { id: "ws2", label: "Finance" },
      { id: "ws3", label: "Information Technology" },
      { id: "ws4", label: "External Comms" },
      { id: "ws5", label: "Tech Ops" },
      { id: "ws6", label: "Legal" },
    ],
    audienceSizes: [
      { id: "as1", label: "Dozens", value: 10 },
      { id: "as2", label: "Hundreds", value: 100 },
      { id: "as3", label: "Thousands", value: 1000 },
      { id: "as4", label: "Ten-thousands", value: 10000 },
      { id: "as5", label: "Hundred-thousands", value: 100000 },
    ],
    impactLevels: [
      { id: "il1", label: "No impact", value: 1 },
      { id: "il2", label: "A small impact", value: 2 },
      { id: "il3", label: "Medium impact", value: 3 },
      { id: "il4", label: "Large impact", value: 4 },
      { id: "il5", label: "Extremely large impact", value: 5 },
    ],
    effortLevels: [
      { id: "el1", label: "Low", value: 1 },
      { id: "el2", label: "Medium", value: 2 },
      { id: "el3", label: "High", value: 3 },
    ],
    // 17 typical end-user personas from the change levers file (Who tab)
    personas: [
      {
        id: "pe1",
        label: "Executives & Senior Leadership",
        desc: "Top-tier decision-makers",
      },
      {
        id: "pe2",
        label: "Business Unit Leaders",
        desc: "Leaders overseeing specific departments",
      },
      {
        id: "pe3",
        label: "People Managers",
        desc: "Supervisory roles overseeing teams",
      },
      {
        id: "pe4",
        label: "Technical & IT Staff",
        desc: "Technology infrastructure & support",
      },
      {
        id: "pe5",
        label: "Frontline Workers",
        desc: "Direct interaction with products, services, customers",
      },
      {
        id: "pe6",
        label: "Administrative & Support Staff",
        desc: "HR, finance, administrative professionals",
      },
      {
        id: "pe7",
        label: "Specialized Roles or Departments",
        desc: "R&D, Marketing, specialized expertise",
      },
      {
        id: "pe8",
        label: "Remote or Distributed Workers",
        desc: "Outside the main office or in varied locations",
      },
      {
        id: "pe9",
        label: "Contractors, Freelancers, Temporary",
        desc: "Non-permanent engagement",
      },
      { id: "pe10", label: "All Employees", desc: "The broadest category" },
      {
        id: "pe11",
        label: "Human Resources",
        desc: "People strategy & employee relations",
      },
      {
        id: "pe12",
        label: "Communications",
        desc: "Delivering employee communications",
      },
      {
        id: "pe13",
        label: "Compliance & Regulatory",
        desc: "Aligning with regulations and policies",
      },
      {
        id: "pe14",
        label: "Project Teams",
        desc: "Implementing the change initiative",
      },
      {
        id: "pe15",
        label: "Training & Development",
        desc: "Designing and delivering training",
      },
      {
        id: "pe16",
        label: "Internal Consultants / SMEs",
        desc: "Deep expertise in specific areas",
      },
      {
        id: "pe17",
        label: "Shareholders / Board",
        desc: "Financial interest or strategic direction",
      },
      {
        id: "pe18",
        label: "External Stakeholders",
        desc: "Clients, customers, suppliers, partners",
      },
    ],
    craCategories: [
      {
        num: 1,
        name: "Vision and Goal",
        question:
          "We have a clear view of what the change initiative is trying to achieve.",
        keysToSuccess:
          "1. Clarity in Vision: For effective change, the vision acts as a north star; its clarity is critical to ensure employees can align their efforts and understand the direction of transformation.\n\n2. Vision-centric Employee Engagement: Engage employees effectively by explaining a transparent roadmap for the future and the milestones of the transformation process.\n\n3. Consistent Leadership Messaging: Emphasize the importance of a consistent and authentic message from senior leaders to foster trust and a sense of unity during change.\n\n4. Enhanced Vision Communication: Elevate communication strategies when necessary to ensure comprehensive employee understanding and buy-in regarding the envisioned change.",
        tacticsR:
          "\u2022 Re-evaluate the Vision: Before re-communicating, ensure the vision itself is clear, compelling, and attainable. Simplify if necessary.\n\n\u2022 Involve Employees: Engage employees in co-creating or refining the vision. When they have a hand in shaping it, they are more likely to understand and commit.\n\n\u2022 Focused Training Sessions: Organize sessions dedicated to understanding the vision, its implications, and its benefits.\n\n\u2022 Visual Representations: Use infographics, videos, and other visual tools to represent the vision. A visual depiction can often be clearer than a lengthy document.\n\n\u2022 Understand the individual change impact: Identify different employee personas (representing different business groups/functions, roles, career levels, geographies, etc.) and complete a stakeholder change impact analysis. ",
        tacticsY:
          "\u2022 Clarify Communication: Review how the vision and goal have been communicated. Consider how to make it clearer or more relatable. Review how the messages are being shared and to whom they\u2019re going.\n\n\u2022 Engage in Workshops: Organize workshops to delve deeper into the vision, allowing a cross-representation (roles, geographies, levels etc.) of employees to explore its meaning and implications. \n\n\u2022 Use Multiple Communication Channels: Use various channels like intranet, newsletters, town halls, and team meetings to regularly communicate and clarify the vision.\n\n\u2022 Leadership Alignment: Ensure all leaders, from top management to front line supervisors, understand and believe in the vision. Provide supporting resources to ensure leaders are prepared to communicate the vision consistently.",
        tacticsG:
          "\u2022 Reinforce Regularly: Even if the vision and goal are well-understood, frequent reinforcement through communications, meetings, and learning sessions can keep the momentum going.\n\n\u2022 Celebrate Milestones: As you reach specific goals or milestones related to the vision, celebrate them. This reinforces the vision, shows progress and keeps employees energized.\n\n\u2022 Encourage Leadership: Leaders should consistently embody the vision in their actions and decisions.\n\n\u2022 Feedback Mechanism: Regularly solicit feedback to ensure the vision remains clear and relevant as the change progresses.",
      },
      {
        num: 2,
        name: "Case for Change",
        question: "People understand why we need to change.",
        keysToSuccess:
          "1. Clear and Compelling: Connect with employees (rationally and emotionally) by presenting a strong and persuasive reason to change. This rationale captures both the hearts and minds of employees.\n\n2. Inclusive: All employees, not just select groups, understand and believe in the need for change.\n\n3. Highlight the Importance: Emphasize the need for change and the risks of maintaining the status quo.\n\n4. Consistent Communication and Leadership: Advocate for routine feedback, maintain open lines of dialogue, and ensure leadership remains coordinated throughout the transformation journey.",
        tacticsR:
          '\u2022 Diagnose the Cause: Before acting, understand the root cause. Is it a communication issue? Do people not understand the "why" behind the change? Have the messages not been shared with the appropriate stakeholders and audiences? Have appropriate stakeholders been engaged?\n\n\u2022 Leadership Alignment: Build alignment around the change by understanding individual leader perspectives, needs, opportunities and challenges. Conduct leadership workshops and provide consistent messaging and communication resources to build understanding, gain buy-in and ensure clarity of vision. \n\n\u2022 Clear, Consistent Communication: Simplify the message if necessary and communicate it consistently across multiple channels. Test messaging to ensure it resonates with different audiences and stakeholders.\n\n\u2022 Seek External Help: Consider hiring change management advisors who can bring an external perspective and proven strategies.\n\n\u2022 Pilot the Change: Start with a small group, implement the change, and highlight its benefits. Success stories can then be used to influence the wider organization.',
        tacticsY:
          "\u2022 Open Dialogue: Host forums, town halls, or workshops where employees can express their ideas, excitement, concerns, ask questions, and get clarifications.\n\n\u2022 Enhance Communication: Clarify the messaging for the change, focusing on the rationale for why, the benefits and the long-term vision.\n\n\u2022 Engage Middle Management: Ensure that managers and front-line team leaders are on board \u2013 they play a crucial role in cascading information down to their teams.\n\n\u2022 Provide Training: Offer training sessions that align with the proposed changes, ensuring everyone has the tools and knowledge they need.",
        tacticsG:
          "\u2022 Reinforce and Reward: Recognize and reward those who are actively supporting and promoting the change. This reinforces the importance of their role.\n\n\u2022 Maintain Communication: Continue sharing success stories, data, and progress reports to keep everyone informed and engaged.\n\n\u2022 Identify Champions: Pinpoint individuals who are particularly passionate about the change and enlist them as change ambassadors.\n\n\u2022 Evaluate and Iterate: Regularly gather feedback (e.g. ongoing change readiness assessments) and be prepared to refine, adjust and/or pivot the change strategy, if necessary.",
      },
      {
        num: 3,
        name: "Change Strategy and Plan",
        question:
          "We have a structured change plan with realistic and achievable timelines.",
        keysToSuccess:
          "1. Change Impact Analysis: Thoroughly evaluate the effects on employees, acknowledging potential hurdles and determining the necessary support mechanisms.\n\n2. Comprehensive Change Strategy: Craft a robust change management approach that outlines success indicators, potential risks, and primary business objectives.\n\n3. Role Clarity: Ensure employees are clear on their role and responsibilities in the context of the impending change.",
        tacticsR:
          "\u2022 Reassess the Strategy: A low score indicates foundational issues. Review and reassess the strategy.\n\n\u2022 Hire External Experts: Consider engaging change management advisors who can provide an outsider's perspective and bring in best practices.\n\n\u2022 Re-engage Leadership: Leaders play a pivotal role in change management. Ensure they are aligned, committed, and vocal supporters of the change.\n\n\u2022 Redevelop Communication Plans: There is a strong possibility that the message is not getting across. Develop a clear, consistent, and comprehensive communication plan. Solicit employee input to ensure the messages are resonating.\n\n\u2022 Involve the Workforce: Engage teams in co-creating parts of the plan. When people have a say in a process, they are more likely to support it.",
        tacticsY:
          "\u2022 Stakeholder Engagement: Engage with key stakeholders to understand their concerns and gather suggestions. Their buy-in can influence others.\n\n\u2022 Task Force or Committee: Consider establishing a change management committee or task force that can delve deeper into potential issues and suggest refinements.\n\n\u2022 Pilot Programs: Before a full-scale rollout, test the plan in smaller teams or departments. Use this as a learning opportunity to iron out any kinks.\n\n\u2022 Resource Alignment: Keep track of resources (e.g., staffing, budget) to ensure there are the levels required to achieve the milestones in the change plan. ",
        tacticsG:
          "\u2022 Refine & Reinforce: Even the best plans can benefit from ongoing refinement. Regularly review and adjust, as necessary.\n\n\u2022 Ongoing Awareness: Ensure that any new members of the team, or those who need refreshers, understand the change strategy and their role.\n\n\u2022 Feedback Loops: Set up mechanisms for continuous feedback from all levels of the organization to ensure any minor issues are caught early and addressed and any identified risks are mitigated.\n\n\u2022 Celebrate Milestones: Recognize and celebrate achievements and milestones as they occur. This boosts morale and emphasizes the importance of the change process.",
      },
      {
        num: 4,
        name: "Organizational Infrastructure and Capability",
        question:
          "We have the capacity and capability to successfully implement the change.",
        keysToSuccess:
          "1. Project Governance: Guide efficient use of resource through strong project governance and management \u2013 move at pace and pivot when required.\n\n2. Skillset roadmap: Ensure a clear plan is in place for delivering the skills needed to implement the change. \n\n3. Operational enablement: Ensure the required systems and processes are in place to enable employees to function as effectively as possible.",
        tacticsR:
          "\u2022 Continuous Training: Focus on keeping the workforce updated with the latest skills, ensuring that they remain competent and adaptive in the latest change management tools/resources.\n\n\u2022 Monitor and Upgrade: Regularly evaluate the technology and process infrastructure. Implement upgrades when necessary to ensure they remain current and efficient (enterprise-wide change assessments, CoE\u2019s, templates, etc.)\n\n\u2022 Feedback Channels: Ensure there are open channels for feedback from employees (especially leaders) about tools, processes, or training they feel might need updating.\n\n\u2022 Documentation: Ensure that all processes, best practices, and protocols are documented. This can be a reference for both current and future employees.",
        tacticsY:
          "\u2022 Gap Analysis: Determine the exact areas where the organization falls short in terms of infrastructure or capability.\n\n\u2022 Targeted Investments: Prioritize and invest in areas that are crucial to the intended change. This might mean new software, tools, or training programs.\n\n\u2022 Engage Middle Management: Middle managers are crucial in translating high-level strategy to ground-level execution. Engage and train them specifically.\n\n\u2022 Collaborative Workshops: Organize workshops where teams can come together to learn, brainstorm, and collaboratively address capability gaps.",
        tacticsG:
          "\u2022 Continuous Training: Focus on keeping the workforce updated with the latest skills, ensuring that they remain competent and adaptive in the latest change management tools/resources.\n\n\u2022 Monitor and Upgrade: Regularly evaluate the technology and process infrastructure. Implement upgrades when necessary to ensure they remain current and efficient (enterprise-wide change assessments, CoE\u2019s, templates, etc.)\n\n\u2022 Feedback Channels: Ensure there are open channels for feedback from employees (especially leaders) about tools, processes, or training they feel might need updating.\n\n\u2022 Documentation: Ensure that all processes, best practices, and protocols are documented. This can be a reference for both current and future employees.",
      },
      {
        num: 5,
        name: "Personal Motivation",
        question: "People are ready and willing to participate in the change.",
        keysToSuccess:
          "1. Empower Employees: Engage and involve employees by giving them the ability to contribute and challenge. \n\n2. Clearly Communicate the Value: Clearly communicate the benefits (personal and organizational) for different stakeholder groups.\n\n3. Align Incentives: Ensure recognition and rewards (financial and non-financial) are aligned to transformation objectives to motivate the desired behaviors to support the change ",
        tacticsR:
          '\u2022 Revisit the Vision: Ensure that the vision and purpose of the change are clear and compelling. If people understand the "why" they are more likely to support the \u201cwhat\u201d and "how".\n\n\u2022 Involve Leaders: Leaders should be role models. Build alignment and sense of ownership for the change(s). Their authentic and visible enthusiasm, commitment, and confidence in the change helps employees get comfortable.\n\n\u2022 Personalized Incentives: Consider introducing incentives (monetary, promotions, recognition) tailored to individual needs to motivate participation.\n\n\u2022 Empathy and Support: Understand that resistance might stem from fear or uncertainty. Offer emotional and logistical support, counseling, or other resources to help employees through their concerns.',
        tacticsY:
          "\u2022 Engage in Dialogues: Organize forums or town halls where employees can share ideas, voice concerns, ask questions, and gain a deeper understanding for the rationale for change.\n\n\u2022 Personalize Learning & Development: Offer learning opportunities that address individual needs, making it more relevant and engaging for each employee.\n\n\u2022 Build Employee Advocacy : Pair employees less motivated with those enthusiastic about the change to build positive energy and understanding.\n\n\u2022 Share Success Stories: Highlighting positive outcomes, even small ones, can inspire and motivate employees who are on the fence about the change.",
        tacticsG:
          "\u2022 Acknowledge and Reward: Recognize and celebrate individuals and teams who are champions of change. This will reinforce their commitment and inspire others.\n\n\u2022 Maintain Open Communication: Continually provide updates about the change process. Keeping employees informed is key to retaining their support.\n\n\u2022 Provide Leadership Opportunities: Empower those who are motivated by giving them leadership roles or opportunities within the change initiative.\n\n\u2022 Feedback Mechanisms: Allow motivated employees to share their experiences, challenges, and suggestions to refine the change process.",
      },
      {
        num: 6,
        name: "Leadership Change Capability",
        question:
          "Leaders are investing as much energy in leading the change as they did determining what needed to change.",
        keysToSuccess:
          "1. Equip managers: Provide managers with essential resources and backing, enabling them to effectively guide their teams during the transition.\n\n2. Visible Leadership: Ensure leaders are highly visible and accessible throughout the transformation and act as early adopters and role models for desired behaviours. \n\n3. Trust-building Leadership: Foster a deeper trust in leadership by emphasizing genuine interactions and upholding mutual respect throughout the change process.",
        tacticsR:
          "\u2022 Clarify Roles and Responsibilities: Provide clear documentation and briefings to define managers' specific roles and responsibilities during the change process.\n\n\u2022 Understand Change Implications: Conduct informational sessions detailing the implications and benefits of the change for both managers and their respective teams.\n\n\u2022 Equip Managers for Guidance: Offer managers comprehensive resources, including talking points, briefing decks, and FAQs, to effectively steer their teams through the transformation.",
        tacticsY:
          "\u2022 Manager Change Toolkit: Create a comprehensive toolkit that includes a change roadmap, role clarification, key messaging, FAQs, and practical advice on navigating change.\n\n\u2022 Change Leadership Training: Implement specialized training sessions that enhance managerial proficiency in leading and facilitating organizational change.\n\n\u2022 Ongoing Data for Managers: Regularly furnish managers with actionable insights from pulse surveys and virtual focus groups to understand and address team needs during the transition.",
        tacticsG:
          "\u2022 Gather Ongoing Manager Feedback: Understand where further support may be needed (e.g., virtual focus groups / pulse surveys / transformation inbox / Q&A sessions)\n\n\u2022 Make Information Access Easy Develop a dedicated manager section of a transformation website providing information updates and easy access to all resources they need to lead the change. \n\n\u2022 Assess and Develop: Conduct leadership assessment and development (e.g., resilient agility assessment) to help managers understand their strengths and areas for development in leading change and provide the project team with a view of manager capability across the organisation.",
      },
      {
        num: 7,
        name: "Communication",
        question:
          "The right messages are being delivered by the right people at the right time.",
        keysToSuccess:
          "1. Tailored Content Delivery: Distribute compelling and user-friendly content strategically, ensuring it reaches the intended audience precisely when it is most impactful.\n\n2. Seamless Information Exchange: Promote a culture where information circulates effortlessly both within teams and across different departments.\n\n3. Culture of Open Dialogue: Cultivate an atmosphere that encourages transparent discussions, values constructive debates, and celebrates shared achievements.",
        tacticsR:
          "\u2022 Change Narrative: Craft an impactful change narrative tailored to distinct audience segments, ensuring resonance with varied stakeholders.\n\n\u2022 Audit Communication Channels : Conduct a thorough audit of current communication channels to identify and maximize the most effective mediums for delivering change messages.\n\n\u2022 Create a Multi-channel Communication Plan: Design a holistic multi-channel communication strategy that integrates varied methods such as emails, webinars, roadshows, and interactive documents, while emphasizing real-time feedback on transformation developments.\n\n\u2022 Clarify Roles and Responsibilities in Communication: Clearly delineate and communicate roles and responsibilities within the team for content creation, approval, and dissemination, ensuring agile and timely communication.",
        tacticsY:
          "\u2022 Transformation Website: Create a central transformation website as a comprehensive resource hub, incorporating features for user feedback.\n\n\u2022 Stakeholder Personas: Craft distinct personas for varied stakeholder groups, utilizing these profiles to shape and refine targeted communication strategies.\n\n\u2022 Change Champion Network: Initiate a network of Change Champions to bolster communication channels and facilitate consistent feedback throughout the transformation process.\n\n\u2022 Employee Listening: Regularly engage in employee listening mechanisms, such as pulse surveys and virtual focus groups, to gather real-time feedback and adjust change and communication strategies accordingly.",
        tacticsG:
          "\u2022 Transformation Website: Launch a transformation website tailored to specific stakeholder groups, ensuring streamlined access to relevant information for both employees and managers.\n\n\u2022 Pulse Survey: Implement a periodic pulse survey to evaluate the success of communication strategies and identify areas for enhancement based on employee feedback.\n\n\u2022 Segmented Communications: Continue to refine and tailor communication content based on individual stakeholder preferences and profiles to boost overall engagement.",
      },
      {
        num: 8,
        name: "Change Measurement",
        question:
          "Measures for the change have been identified and will be tracked and communicated.",
        keysToSuccess:
          "1. Define Success Indicators: From the onset, establish the critical indicators that will signify a successful change.\n\n2. Monitoring Progress and Build Flexibility: Continuously track and share readiness progression with stakeholders, adapting the readiness strategy as insights emerge.\n\n3. Build Employee Engagement and Resilience: Clarify change-related expectations for employees and foster a culture that champions open dialogue and iterative learning during the readiness phase.",
        tacticsR:
          "\u2022 Agree on Measures: Establish specific metrics for gauging change, such as business performance, past project data, and employee feedback, while defining success criteria for each.\n\n\u2022 Track and Report: Systematically monitor and communicate the identified metrics at predetermined intervals to evaluate progress and determine any necessary adjustments to the change strategy.\n\n\u2022 Incorporate into Existing Channels: Integrate transformation-related inquiries into pre-existing employee feedback mechanisms like annual evaluations and ongoing pulse surveys.",
        tacticsY:
          "\u2022 Ongoing Tracking: Continuously monitor and communicate key metrics during the project to guide the change strategy and inform any course corrections.\n\n\u2022 Progress Updates: Regularly inform employees about advancements concerning pivotal measures to maintain transparency and engagement.\n\n\u2022 Pulse Surveys/Focus Groups: Implement periodic pulse surveys and virtual focus groups to gauge employee perceptions of the transformation, subsequently reporting on remedial actions.\n\n\u2022 Embed Objectives: Integrate transformation-specific goals into the objectives of leaders, managers, and employees to foster desired behaviors and outcomes.",
        tacticsG:
          "\u2022 Change Dashboard: Create a dynamic dashboard highlighting pivotal metrics for consistent reporting and analysis to the change steering committee.\n\n\u2022 Data Access for Leaders: Grant leaders and managers consistent access to relevant data, objectives, and team-specific metrics to fine-tune their change management approaches.\n\n\u2022 Updates to Employees: Disseminate periodic updates to staff about performance metrics, tailoring information to specific teams where necessary.\n\n\u2022 Review Decision Rights: Assess and adjust decision-making rights to ensure individuals are fully empowered to drive transformation and effectively fulfil their designated roles.",
      },
    ],
    sizingQuestions: [
      {
        num: 1,
        text: "How many employees are impacted by this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Dozens",
          "Hundreds",
          "Thousands",
          "Ten-thousands",
          "Hundred-thousands",
        ],
      },
      {
        num: 2,
        text: "What is the magnitude of the change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Single impact, single audience",
          "Multiple impacts, single audience",
          "Multiple impacts, multiple audiences",
          "Multiple change programs, single audience",
          "Multiple change programs, multiple audiences",
        ],
      },
      {
        num: 3,
        text: "How many changes are currently underway with your impacted audiences?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 4,
        text: "How positively or negatively will impacted audience(s) react to the implementation of this change?",
        category: "impact",
        reverse: true,
        scale: [
          "Very negatively",
          "Negatively",
          "Neither negative nor positive",
          "Positive",
          "Very positive",
        ],
      },
      {
        num: 5,
        text: "How involved will HR leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 6,
        text: "How involved will corporate leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 7,
        text: "How involved will business unit leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 8,
        text: "How many HR resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 9,
        text: "How many corporate resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 10,
        text: "How many business unit resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 11,
        text: "In general, how effective are leaders and/or managers at leading change?",
        category: "impact",
        reverse: false,
        scale: [
          "Not at all effective",
          "Somewhat effective",
          "Effective",
          "Very effective",
          "Extremely effective",
        ],
      },
      {
        num: 12,
        text: "How big of an impact will this change have on employees (e.g., new skills, roles, culture, technology)?",
        category: "impact",
        reverse: false,
        scale: [
          "No impact",
          "A small impact",
          "Medium impact",
          "Large impact",
          "Extremely large impact",
        ],
      },
      {
        num: 13,
        text: "How big of an impact will this change have on leaders?",
        category: "impact",
        reverse: false,
        scale: [
          "No impact",
          "A small impact",
          "Medium impact",
          "Large impact",
          "Extremely large impact",
        ],
      },
    ],
    sizingInterventions: [
      { phase: "Define", name: "Case for Change and Key Messages", tier: 3 },
      { phase: "Define", name: "Change Readiness Assessment", tier: 3 },
      { phase: "Define", name: "Measurement Approach", tier: 3 },
      { phase: "Discover", name: "Stakeholder Assessment", tier: 3 },
      {
        phase: "Discover",
        name: "Change Impact Assessment - High Level",
        tier: 3,
      },
      { phase: "Design", name: "Stakeholder Engagement Plan", tier: 3 },
      { phase: "Design", name: "Change Management Plan", tier: 3 },
      {
        phase: "Design",
        name: "Change Management Deliverables Creation",
        tier: 3,
      },
      { phase: "Transform", name: "Deliverables Rollout", tier: 3 },
      { phase: "Transform", name: "Measurement dashboard", tier: 3 },
      {
        phase: "Discover",
        name: "Change Impact Assessment - Detailed",
        tier: 2,
      },
      { phase: "Discover", name: "Stakeholder Alignment", tier: 2 },
      { phase: "Design", name: "Persona Development", tier: 2 },
      { phase: "Discover", name: "Training Needs Analysis", tier: 1 },
      { phase: "Discover", name: "Ease/Benefit Matrix", tier: 1 },
      { phase: "Design", name: "Change Agent Network Development", tier: 1 },
      { phase: "Transform", name: "Kick-off Change Agent Network", tier: 1 },
    ],
  },
  reactionLeverMap: {
    r1: { primary: "l2", secondary: "l4" },
    r2: { primary: "l1", secondary: "l5" },
    r3: { primary: "l3", secondary: "l1" },
    r4: { primary: "l3", secondary: "l5" },
    r5: { primary: "l3", secondary: "l1" },
    r6: { primary: "l2", secondary: "l1" },
    r7: { primary: "l5", secondary: "l1" },
    r8: { primary: "l4", secondary: "l3" },
  },
  changes: [
    {
      id: "c1",
      name: "Leadership Reorganization",
      isKey: true,
      description:
        "Restructure leadership roles post-acquisition; integrate SoftWave senior leaders into TechLoom org structure. Define new operating model, cascade reporting lines, align comp bands.",
    },
    {
      id: "c2",
      name: "Engineering Platform Consolidation",
      isKey: true,
      description:
        "Migrate SoftWave engineering teams from legacy stack onto TechLoom unified DevOps platform. Standardize CI/CD, code review, release processes. Six-month phased migration starting Q2.",
    },
    {
      id: "c3",
      name: "HR Systems Harmonization",
      isKey: false,
      description:
        "Single source of truth for HRIS. Consolidate SoftWave HR data into TechLoom Workday instance; unified payroll, benefits, and time-off systems.",
    },
  ],
  changesToImplement: [
    {
      id: "ci1",
      changeId: "c1",
      name: "Define new leadership team",
      description:
        "Identify and announce combined leadership team across both organizations; clarify scope of each role.",
      workstreamId: "ws1",
      from: "Two separate executive teams operating independently",
      to: "Unified leadership team with clear remit and reporting structure",
    },
    {
      id: "ci2",
      changeId: "c1",
      name: "Cascade org structure",
      description:
        "Cascade the new organizational structure through directors and managers to all employees.",
      workstreamId: "ws1",
      from: "Legacy reporting lines from each organization",
      to: "Single unified reporting structure visible to all employees",
    },
    {
      id: "ci3",
      changeId: "c2",
      name: "Migrate to unified DevOps platform",
      description:
        "Phased migration of SoftWave engineering teams onto TechLoom platform; team-by-team cutover with old-stack support during transition.",
      workstreamId: "ws3",
      from: "SoftWave on legacy GitLab + CircleCI; TechLoom on internal platform",
      to: "All engineering teams on TechLoom unified DevOps platform",
    },
    {
      id: "ci4",
      changeId: "c2",
      name: "Code review standards alignment",
      description:
        "Unify code review and release process across both engineering organizations; jointly-defined standards.",
      workstreamId: "ws3",
      from: "Two different code review cultures and release cadences",
      to: "Single set of jointly-owned engineering standards",
    },
    {
      id: "ci5",
      changeId: "c3",
      name: "HRIS data migration",
      description:
        "Consolidate SoftWave HR data into TechLoom Workday; unified payroll, benefits, time-off. Single hard cutover.",
      workstreamId: "ws1",
      from: "SoftWave on BambooHR; TechLoom on Workday",
      to: "All employees on TechLoom Workday with consolidated benefits",
    },
  ],
  audiences: [
    {
      id: "a1",
      changeToImplementId: "ci1",
      name: "Executives and Senior Leadership",
      impactDescription:
        "Reorganization of leadership roles and responsibilities. Senior leaders take on new portfolios, in some cases reporting to former peers. High personal stakes; high visibility internally. Their public alignment (or visible discomfort) sets the tone for every level below.",
      audienceType: "end_user",
      personaId: "pe1",
      roleId: "ro1",
      functionId: "f8",
      levelId: "lv1",
      country: "United States",
      region: "Americas",
      audienceSizeId: "as1",
      startDate: "2026-01-15",
      endDate: "2026-04-30",
      impactLevelId: "il5",
      effortLevelId: "el3",
      impactTypeIds: ["it1", "it6"],
      reactionIds: ["r4", "r1"],
      leverIds: ["l3", "l5", "l6"],
      know: "The strategic rationale for the integration; the specific shape of the new operating model; their individual role, scope, comp, and reporting line; the timeline for cascade to their orgs; the talking points they're expected to deliver.",
      feel: "Heard and consulted on the design (not informed after the fact); confident in their own role and ability to succeed in it; respectful of the SoftWave leaders joining the team; willing to publicly model 'one company' behavior even when private discomfort exists.",
      doIt: "Attend the leadership offsite Jan 27-28; complete 1:1s with Maya by Feb 6; co-host cascade town halls with their counterparts by Feb 28; reinforce the integration narrative in every all-hands and external interview.",
      voiceQuote:
        "\"I'm in. I'm just trying to figure out which fights to pick and which ones to let go — and I'd rather know that now than three months from now when the dust has settled into the wrong shape.\"",
      concerns:
        "What is my actual scope vs my old scope? Does my comp reflect the new role? Where will I disagree with my new peer, and what's the escalation path when we can't agree? How visible will the SoftWave leaders be vs me? If I'm replaced or moved within 12 months, what's the financial commitment? Who decides who keeps the office vs who works from home?",
      successSignals:
        "First contested decision goes their way (even just one) so they know they have real authority. Their team sees them publicly aligned with their cross-org counterpart at the next all-hands. Their year-end review explicitly addresses the integration transition, not just regular goals. They get one meaningful 1:1 with Maya within their first 30 days.",
    },

    {
      id: "a2",
      changeToImplementId: "ci2",
      name: "Business Unit Leaders",
      impactDescription:
        "Need to realign team objectives with integrated org and build relationships with SoftWave counterparts. Will be expected to model new ways of working publicly. Many manage P&Ls that change shape under the new structure — their year-end targets are mid-flight.",
      audienceType: "end_user",
      personaId: "pe2",
      roleId: "ro2",
      functionId: "f8",
      levelId: "lv2",
      country: "United States",
      region: "Americas",
      audienceSizeId: "as2",
      startDate: "2026-02-01",
      endDate: "2026-06-30",
      impactLevelId: "il4",
      effortLevelId: "el2",
      impactTypeIds: ["it1", "it6"],
      reactionIds: ["r6", "r2"],
      leverIds: ["l2", "l1"],
      know: "How their BU's scope, P&L, and headcount changes in the new structure; how their goals map to the integrated company's metrics; who their new peers are and what's expected of joint cross-BU work.",
      feel: "Energized by the larger scope; trusting that decisions on contested territory will be made fairly; ready to advocate for their teams while still holding the integrated company first.",
      doIt: "Meet 1:1 with each of their SoftWave counterparts within 30 days; jointly publish a 'how we'll work together' charter by end of Q1; attend the leadership integration offsite; pull their direct reports through the manager cascade kit.",
      voiceQuote:
        "\"My P&L just got redrawn mid-year. I need to know whose number I'm carrying, who I'm partnered with on the cross-sell motion, and whether the comp plan changes were modeled against the new structure or the old one.\"",
      concerns:
        "What do my year-end numbers look like under the new structure — am I being measured on the old plan or a new one? Who has the final call when my counterpart and I disagree on territory? If my BU shrinks, what happens to my headcount? Why are we restructuring before we hit Q4 targets? How is the new structure being communicated externally to my customers?",
      successSignals:
        "Their cross-BU partnership produces one visible joint win within 90 days. Their year-end target is restated cleanly under the new structure (not left ambiguous). Their team's headcount doesn't shrink without explicit conversation. Their voice is heard in the offsite agenda design, not just in the room.",
    },

    {
      id: "a3",
      changeToImplementId: "ci3",
      name: "SoftWave Engineering Staff",
      impactDescription:
        "Introduction of new tools and integration protocols on the TechLoom platform. Most have used the legacy stack for 3-5 years; muscle memory + tooling preferences run deep. The migration is happening while they continue to ship product against existing roadmap commitments — capacity is the real constraint.",
      audienceType: "end_user",
      personaId: "pe4",
      roleId: "ro4",
      functionId: "f3",
      levelId: "lv5",
      country: "United Kingdom",
      region: "Europe",
      audienceSizeId: "as3",
      startDate: "2026-04-15",
      endDate: "2026-09-30",
      impactLevelId: "il4",
      effortLevelId: "el3",
      impactTypeIds: ["it3", "it4", "it5"],
      reactionIds: ["r3", "r5", "r4"],
      leverIds: ["l3", "l1", "l5"],
      know: "Their specific team's migration date; what stays and what changes in their day-to-day workflow; where to get help when stuck; the realistic expectation for productivity dip during transition; the timeline of when the old stack will be turned off.",
      feel: "Respected as engineers (not condescended to with over-simplified training); supported with real help during the migration; confident the new platform will be at least as good as the old one (or honest about where it won't be).",
      doIt: "Complete the platform onboarding pathway within 30 days of their team's migration date; attend the hands-on training sessions; raise blockers in the weekly DevOps check-in rather than absorbing them silently; help onboard the team that migrates after them.",
      voiceQuote:
        "\"I came to SoftWave because the tooling was good and the bullshit was low. If 'unified DevOps' means I'm waiting on a ticket queue to get my CI changed, I'd rather know now so I can plan accordingly.\"",
      concerns:
        "Will my deploys take longer on the new stack? What happens to the custom CI we built — does any of it survive, or do we just throw it away? Will I still report to my current manager? Why are we doing this during our biggest product push of the year? Is this actually better, or is it just consolidation for consolidation's sake? Who do I escalate to when the new toolchain has a bug?",
      successSignals:
        "Their first deploy on the new stack completes faster than their last one on the old stack (or at least not measurably slower). The first ticket they file against the new toolchain gets a response within 24 hours. Their team's velocity recovers to baseline within 6 weeks of migration. One of their previously-frustrating tools is meaningfully improved. They keep their current manager.",
    },

    {
      id: "a4",
      changeToImplementId: "ci3",
      name: "SoftWave Engineering Managers",
      impactDescription:
        "Lead their teams through tooling migration while learning the new platform themselves. Doubled cognitive load during a busy product roadmap. They are the single biggest predictor of how well their teams ride out the change — and they need support, not just expectations.",
      audienceType: "project_user",
      personaId: "pe3",
      roleId: "ro2",
      functionId: "f3",
      levelId: "lv3",
      country: "United Kingdom",
      region: "Europe",
      audienceSizeId: "as1",
      startDate: "2026-03-01",
      endDate: "2026-09-30",
      impactLevelId: "il4",
      effortLevelId: "el3",
      impactTypeIds: ["it3", "it5"],
      reactionIds: ["r3", "r4"],
      leverIds: ["l3", "l1", "l5"],
      know: "The full migration plan deeply (not just the highlight reel); the specific tradeoffs being made and why; what flexibility they have to sequence their team's transition; how to talk to skeptical engineers without inflating their concerns.",
      feel: "Trusted as professionals; supported (not surveilled) by their leadership; able to be honest with their team without sounding like spokespeople reading scripts.",
      doIt: "Attend the manager-only briefing two weeks ahead of their team's migration date; complete the manager cascade conversation guide; surface team-level blockers in the weekly leadership check-in; visibly model use of the new platform themselves.",
      voiceQuote:
        '"I need answers, not talking points. My team will ask me hard questions and they can tell the difference between me knowing something and me reading a slide."',
      concerns:
        "What do I tell my best engineer when they ask if it's time to look elsewhere? Do I have any say in my team's migration date, or is it dropped on me? If my team falls behind on the migration, is that a performance issue for me? How do I run a team where some people are excited and others are quietly job-hunting? When am I going to do real work — between migration meetings, hiring, and my own learning curve, my calendar is full?",
      successSignals:
        "They get answers (not just empathy) at the manager-only briefings. Their team's migration pace is something they can influence, not just absorb. They have one peer manager (cross-org) they can text honestly. Their team retention through migration is recognized as a result, not assumed. Their workload during migration is acknowledged and adjusted, not just stacked.",
    },

    {
      id: "a5",
      changeToImplementId: "ci4",
      name: "Senior Engineers (Both Orgs)",
      impactDescription:
        "Adapt to unified code review and release process. Will be expected to coach junior engineers through the change while adapting themselves. Their endorsement (or visible skepticism) of the new standard is what determines whether the change takes hold organically or has to be enforced.",
      audienceType: "end_user",
      personaId: "pe4",
      roleId: "ro3",
      functionId: "f3",
      levelId: "lv3",
      country: "Multiple",
      region: "Global",
      audienceSizeId: "as2",
      startDate: "2026-04-01",
      endDate: "2026-07-31",
      impactLevelId: "il3",
      effortLevelId: "el2",
      impactTypeIds: ["it5"],
      reactionIds: ["r1", "r5"],
      leverIds: ["l5", "l3", "l4"],
      know: "The full draft of the unified standards (not summaries); the decision rationale on contested items where their org's practice wasn't picked; the timeline for review and feedback; how their feedback will be incorporated (or not).",
      feel: "Heard on the technical merits; respected as the senior voice in the room; willing to coach others through the change rather than just complying themselves.",
      doIt: "Participate in the code review pairing pilots in late April; review draft standards by May 1 with detailed feedback; mentor at least two junior engineers through the transition; flag standards in the docs that don't make sense in practice.",
      voiceQuote:
        "\"Show me the code. Don't tell me about the new 'standards' — tell me which lint rules I'm fighting, what my CI looks like, and what happens when I disagree with the unified standard on something that matters.\"",
      concerns:
        "Whose standards 'won' on the contested items, and why? Was the decision technical or political? Will my code style get rewritten by an automated migration script? Do I get a vote on the standards or just a complaint window? Will my reviews from the other org's seniors be substantively useful or just rubber-stamps?",
      successSignals:
        "At least one major standards decision visibly goes against the TechLoom default in favor of a SoftWave practice (proves the council isn't rigged). Their code-review pairing produces a real disagreement that gets resolved with reasoning, not authority. The unified standard has more concrete examples than the old TechLoom one did. Their feedback on the draft standards visibly changes the final version.",
    },

    {
      id: "a6",
      changeToImplementId: "ci2",
      name: "HR Business Partners",
      impactDescription:
        "Support engineering organization through cultural and tooling change. Need fluency in both companies' cultural norms to mediate effectively. They are the early warning system for cultural friction — and the first people line managers call when something feels off.",
      audienceType: "project_user",
      personaId: "pe11",
      roleId: "ro3",
      functionId: "f1",
      levelId: "lv3",
      country: "Multiple",
      region: "Global",
      audienceSizeId: "as1",
      startDate: "2026-02-01",
      endDate: "2026-12-31",
      impactLevelId: "il3",
      effortLevelId: "el2",
      impactTypeIds: ["it4", "it5"],
      reactionIds: ["r2"],
      leverIds: ["l5", "l3"],
      know: "The cultural baseline of each org (what's distinctive, what's at risk); the people-side of the migration timeline; their named counterpart on the other side; the escalation path when something concerning surfaces.",
      feel: "Empowered to flag concerns early; supported when they have to deliver hard messages; trusted as the bridge across the two cultures.",
      doIt: "Attend the cross-org HRBP onboarding workshop in February; pair-shadow a SoftWave-side counterpart for a week; produce a monthly 'pulse summary' of cultural-integration signals; flag emerging risks in the bi-weekly steering committee.",
      voiceQuote:
        '"I can already tell you which managers are going to mishandle the cascade conversations. The question is whether anyone wants to hear it before they happen."',
      concerns:
        "When I flag a cultural risk in steering, does it actually change anything or just go in the minutes? Who has authority to slow a workstream if HR concerns aren't being addressed? What's my role when a manager is doing the wrong thing and their senior leader won't intervene? Am I supposed to be a partner to the business or a check on it? How am I supposed to track 'culture' when my main metrics are headcount and time-to-fill?",
      successSignals:
        "One of their flagged risks demonstrably changes a project decision (proves their voice has weight). They have a cross-org HRBP peer they actually trust. Steering meetings reserve time for HR commentary, not just status. Their monthly pulse summary is read and referenced by leadership. They aren't the last to know when a major decision affects their population.",
    },

    {
      id: "a7",
      changeToImplementId: "ci5",
      name: "Payroll Operations Team",
      impactDescription:
        "Execute the technical migration of SoftWave payroll into TechLoom Workday. Data quality, tax-jurisdiction mapping, comp-history reconciliation. Every error in this workstream eventually shows up in someone's paycheck — the work has no tolerance for surprises and a hard cutover date.",
      audienceType: "project_user",
      personaId: "pe6",
      roleId: "ro5",
      functionId: "f1",
      levelId: "lv4",
      country: "United States",
      region: "Americas",
      audienceSizeId: "as1",
      startDate: "2026-06-01",
      endDate: "2026-09-30",
      impactLevelId: "il4",
      effortLevelId: "el3",
      impactTypeIds: ["it3", "it4"],
      reactionIds: ["r3", "r5"],
      leverIds: ["l3", "l1"],
      know: "The full data mapping spec; the cutover plan including backup/rollback procedures; the parallel-run schedule; the specific UK tax-jurisdiction edge cases that will need manual reconciliation; their staffing plan for the cutover week.",
      feel: "Resourced for the load (not asked to absorb the spike with goodwill); trusted to flag issues without being labeled obstructionist; supported with backup coverage during the cutover.",
      doIt: "Complete data validation passes on time; flag mapping ambiguities to the integration team within 24h of discovery; staff overtime coverage for the first two pay cycles post-cutover; document anomalies for post-mortem review.",
      voiceQuote:
        "\"The day a paycheck is wrong, nobody cares whose fault it actually was. The CEO gets an email and we get the call. So I need the data integrity story to be airtight before cutover, not 'mostly fine.'\"",
      concerns:
        "Who owns it if a paycheck is wrong on day one — me, the integration team, or the vendor? Do we get backup coverage for the first two pay cycles, or are we expected to absorb the spike? What's the rollback plan if data integrity issues are found in the first run? Are we testing with real historical data or synthetic data? Why is the cutover scheduled during quarter-end?",
      successSignals:
        "First parallel run has zero paycheck delta vs the old system (or, if not zero, every difference is documented and explainable). Cutover staffing includes guaranteed backup from the integration team for 14 days. There's a written rollback procedure that's been rehearsed. Quarter-end isn't on the cutover date. Their team gets visible credit for a clean cutover at the all-hands.",
    },

    {
      id: "a8",
      changeToImplementId: "ci5",
      name: "All Employees",
      impactDescription:
        'New self-service HR portal; new benefits enrollment process; one-time data verification ask during cutover. Low-impact change but affects everyone. Mostly a "don\'t-screw-it-up" workstream — done well, nobody notices; done poorly, every employee has a story.',
      audienceType: "end_user",
      personaId: "pe10",
      roleId: "ro7",
      functionId: "f8",
      levelId: "lv8",
      country: "Multiple",
      region: "Global",
      audienceSizeId: "as3",
      startDate: "2026-08-01",
      endDate: "2026-10-31",
      impactLevelId: "il2",
      effortLevelId: "el1",
      impactTypeIds: ["it3"],
      reactionIds: ["r2"],
      leverIds: ["l1", "l3"],
      know: "Where the new portal is; what they need to do during the verification window (and when); who to call if something looks wrong; what's NOT changing (their paycheck amount, their benefits selections, their PTO balance).",
      feel: "Confident the change won't disrupt their pay or benefits; not anxious about having to learn a complicated new system; quietly served by HR rather than asked to do a lot of work.",
      doIt: "Bookmark the new portal URL; verify their personal record during the two-week verification window; complete benefits re-elections by the deadline if applicable; raise issues through the new HR helpdesk.",
      voiceQuote:
        "\"As long as my paycheck shows up correctly and I can still take vacation, I don't really care which system is behind it. Just don't make me re-enter my emergency contact for the fifth time.\"",
      concerns:
        "Will my paycheck arrive on time? Did my time-off balance transfer correctly? Do I have to re-enter my benefits elections from scratch, or do my current selections roll over? What happens to the personal information I had in BambooHR — does it actually delete, or just sit somewhere? Why is the new portal slower than the old one (if it is)?",
      successSignals:
        "Their first paycheck on the new system arrives on time and is correct. Their time-off balance shows up correctly on day one. They can complete their first self-service task (e.g., updating an address) in under 2 minutes without needing help. The 'what changed' reference card actually arrives before the cutover (not after). They don't see a single broken page in the new portal in their first week.",
    },

    {
      id: "a9",
      changeToImplementId: "ci1",
      name: "SoftWave Founders",
      impactDescription:
        "Transition from autonomous founder-leaders to integrated members of TechLoom leadership team. Loss of ultimate authority over direction is a real psychological shift. They are also the human signal SoftWave employees will read most carefully — their public posture matters more than any leadership announcement.",
      audienceType: "end_user",
      personaId: "pe1",
      roleId: "ro1",
      functionId: "f8",
      levelId: "lv1",
      country: "United Kingdom",
      region: "Europe",
      audienceSizeId: "as1",
      startDate: "2026-01-15",
      endDate: "2026-12-31",
      impactLevelId: "il5",
      effortLevelId: "el3",
      impactTypeIds: ["it1", "it6"],
      reactionIds: ["r1", "r4", "r6"],
      leverIds: ["l5", "l3", "l4"],
      know: "Their concrete authority and decision rights in the new structure (not vague 'partner' language); the brand transition timeline; the specific commitments TechLoom is making about SoftWave's distinctive practices; their personal coaching/support resources.",
      feel: "Respected, not patronized; useful, not ceremonial; trusted to disagree publicly when it matters; supported privately through what is genuinely a hard transition.",
      doIt: "Engage with the executive coach (Priya bi-weekly, James monthly); co-deliver the all-hands announcement; visibly use the new tools and new branding; raise concerns privately with Maya rather than venting publicly; help identify which SoftWave practices should be preserved and championed.",
      voiceQuote:
        "\"I'm not against any of this. I just keep watching things that mattered to us — the Friday demos, the way we hired, the standard for what 'shipping' meant — get quietly de-prioritized in favor of 'how TechLoom does it.' I'm fine with the integration. I'm not fine with disappearing.\"",
      concerns:
        "What specifically gets preserved from what we built — and is it actually preserved, or just acknowledged in a memo? How do I disagree with Maya without seeming like I'm relitigating the acquisition? What do I tell people who joined SoftWave because of us, who are now reporting to TechLoom managers? Am I a real executive in this company or a transitional figurehead? When does the brand actually retire, and is that being communicated honestly to our customers?",
      successSignals:
        "At least one distinctively-SoftWave practice (e.g., Friday demos) explicitly survives the integration, by name. They are visibly present in joint communications, not just CC'd. Their feedback in the first 90 days demonstrably influences a leadership decision. They have a real coaching relationship that isn't performative. The brand retirement is handled with respect and clear customer communication, not announced at the last minute.",
    },

    {
      id: "a10",
      changeToImplementId: "ci2",
      name: "External Enterprise Customers",
      impactDescription:
        "Continuity of service during platform migration. Some may need to update integrations; account managers must communicate proactively. The wrong customer hearing about migration risk from someone other than their CSM is the failure mode to avoid.",
      audienceType: "end_user",
      personaId: "pe18",
      roleId: "ro7",
      functionId: "f6",
      levelId: "lv8",
      country: "Multiple",
      region: "Global",
      audienceSizeId: "as3",
      startDate: "2026-05-01",
      endDate: "2026-11-30",
      impactLevelId: "il3",
      effortLevelId: "el2",
      impactTypeIds: ["it3"],
      reactionIds: ["r2"],
      leverIds: ["l1", "l5"],
      know: "What the integration means for their service (in 95% of cases: nothing); the integration timeline; any API or contract changes affecting their specific implementation; their dedicated point of contact for questions.",
      feel: "Reassured that service quality is not at risk; informed without being alarmed; appreciated as a continuing customer.",
      doIt: "Read the customer communication when it lands; raise questions through their CSM (not through social or press); update integrations only when their dedicated communication tells them to.",
      voiceQuote:
        "\"We don't actually care about your integration. We care about whether our integration still works on Monday morning. Tell us what's changing for us, what isn't, and who to call if it breaks.\"",
      concerns:
        "Will the API contract change? Will our SLAs change? Will our pricing change? Will our account manager change? Who do I call if something breaks during the migration? Should I be worried about data residency given the UK/US combination? Is this a sign of cost pressure that affects our renewal?",
      successSignals:
        "Their CSM calls them before they see anything in the press. The migration causes zero observable change to their service. Their API contract is explicitly confirmed unchanged in writing. Any contract changes (if any) are surfaced 90+ days before they take effect. The first interaction they have with the 'new' company feels at least as good as the old one.",
    },

    {
      id: "a11",
      changeToImplementId: "ci4",
      name: "Junior Engineers (TechLoom)",
      impactDescription:
        "Mentor SoftWave engineers through standards alignment. Opportunity to demonstrate technical leadership earlier than typical career path. Low risk, high upside group — this is one of the rare audiences where the change is mostly good news for them.",
      audienceType: "end_user",
      personaId: "pe4",
      roleId: "ro3",
      functionId: "f3",
      levelId: "lv6",
      country: "United States",
      region: "Americas",
      audienceSizeId: "as2",
      startDate: "2026-05-01",
      endDate: "2026-08-31",
      impactLevelId: "il2",
      effortLevelId: "el1",
      impactTypeIds: ["it5"],
      reactionIds: ["r8"],
      leverIds: ["l4", "l3"],
      know: "Where the mentoring opportunity counts toward promotion criteria; the boundaries of their role (mentor in TechLoom practices, not architect the new standard); how to give peer feedback to engineers more senior than themselves.",
      feel: "Recognized for what they know; growing in confidence; supportive of the SoftWave engineers they're paired with rather than competitive.",
      doIt: "Sign up for the peer-pairing pilot; spend 2-3 hours/week mentoring through the migration period; flag any pairing dynamics that aren't working; submit a write-up of what they learned for the manager portal.",
      voiceQuote:
        "\"This is kind of cool — I get to be the expert for once. But I also don't want to be the one explaining to a Staff-level engineer why their old CI is being deprecated. I'd rather they get that answer from someone with more seniority than me.\"",
      concerns:
        "Will this 'mentoring' count toward promotion, or is it just extra work? What if my pairing partner is grumpy about being mentored by someone junior? Will I get pulled off my regular work to help, and is my manager OK with that? Am I supposed to teach the new stack, or also handle the emotional side of someone's old tools being deprecated?",
      successSignals:
        "Mentoring time is explicitly logged toward promotion criteria. Their pairing is matched on temperament as well as topic. Their manager publicly acknowledges the time investment. They get genuine peer feedback from their senior pair, not just 'thanks for explaining.' At least one of their write-ups gets adopted into the official manager portal.",
    },

    {
      id: "a12",
      changeToImplementId: "ci1",
      name: "People Managers",
      impactDescription:
        "Cascade messaging about the new structure to direct reports; field questions; reinforce the rationale. The hinge between strategy and execution. They will face questions they don't have answers to and will need permission to say \"I don't know yet — and here's how I'll find out.\"",
      audienceType: "project_user",
      personaId: "pe3",
      roleId: "ro2",
      functionId: "f8",
      levelId: "lv3",
      country: "Multiple",
      region: "Global",
      audienceSizeId: "as2",
      startDate: "2026-02-15",
      endDate: "2026-05-31",
      impactLevelId: "il3",
      effortLevelId: "el2",
      impactTypeIds: ["it1", "it5"],
      reactionIds: ["r3", "r2"],
      leverIds: ["l3", "l1"],
      know: "The new org structure including where the answer to common questions can be found; what's been decided vs. still in flight; permission to escalate questions they can't answer; the dates of cascade conversations they're expected to lead.",
      feel: "Trusted by their leadership to land the message authentically; equipped (toolkit + practice + Q&A access); permitted to be human (acknowledge concerns, not paper over them).",
      doIt: "Attend the manager briefing in Feb; hold 1:1s with each direct report about what the change means for them within 10 business days of the cascade; raise unanswered questions in the weekly manager forum; use the talking points but don't read them robotically.",
      voiceQuote:
        "\"I'm supposed to deliver the message, answer the questions, manage the morale, hit my own numbers, and not look like I don't have answers when I genuinely don't have answers. That's an unusual ask, even for a manager.\"",
      concerns:
        "What do I say when someone asks 'is my job safe?' and I genuinely don't know? Will I be evaluated on how well my team takes the change? Will the cascade conversations show up in someone's pulse survey and reflect on me even if my team's circumstances are unusual? Why do I always find out 24 hours before my team does, instead of a week in advance? Is the 'manager-only' Slack channel actually private?",
      successSignals:
        "The manager-only forum is genuinely manager-only (no senior eavesdropping). They learn what's coming before their team does, with enough lead time to prepare. Their HRBP has bandwidth for emergency calls during cascade weeks. The talking points include an explicit 'if you don't know, say so' frame. One peer manager publicly handles a tough question well and the example is shared (anonymized) so others can learn from it.",
    },

    {
      id: "a13",
      changeToImplementId: "ci3",
      name: "Compliance & Legal",
      impactDescription:
        "Review tooling migration for data governance compliance. GDPR considerations given SoftWave is UK-based and TechLoom is US-headquartered. Their pace constraints aren't friction — they're the constraint the project has to be planned around, not over.",
      audienceType: "project_user",
      personaId: "pe13",
      roleId: "ro3",
      functionId: "f7",
      levelId: "lv3",
      country: "United Kingdom",
      region: "Europe",
      audienceSizeId: "as1",
      startDate: "2026-03-01",
      endDate: "2026-07-31",
      impactLevelId: "il3",
      effortLevelId: "el2",
      impactTypeIds: ["it2"],
      reactionIds: ["r5"],
      leverIds: ["l1", "l3"],
      know: "The full data-flow architecture of the new platform; the timeline of when each compliance checkpoint is needed; how to flag concerns without triggering full project re-plans; who has authority to accept residual risk.",
      feel: "Treated as partners (involved early), not gatekeepers (consulted late); confident in the documentation trail; supported when they have to deliver hard answers.",
      doIt: "Sign off on data architecture by April 15; provide a written GDPR risk memo by May 1; attend the bi-weekly compliance checkpoint; flag any architecture changes that affect their assessment as soon as they hear about them.",
      voiceQuote:
        '"I\'m not trying to slow this down. I just need to be in the room when decisions are made, not asked to bless them after the architecture diagram is already in production."',
      concerns:
        "Are we routing UK employee data through US infrastructure in a way that creates GDPR exposure? Who is the controller and who is the processor in the new architecture? What does the data processing agreement with the cloud provider look like? Has the right of erasure been engineered into the new HRIS, or just promised? Are we documenting our lawful basis for each data flow?",
      successSignals:
        "They're brought in at architecture design phase, not at sign-off phase. They have a written commitment to their checkpoint cadence (not 'we'll loop you in'). Their risk memo is visibly read and addressed point-by-point, not just acknowledged. One high-severity issue they raise visibly delays a phase gate (proves the process works). Their team's bandwidth is respected with precise sign-off scope, not blanket review asks.",
    },

    {
      id: "a14",
      changeToImplementId: "ci5",
      name: "HR Service Center Reps",
      impactDescription:
        "Field employee questions during HRIS transition. Spike in ticket volume expected during cutover and the two weeks after. Volume is one thing; the harder problem is the emotional content of the calls when paychecks or benefits look wrong.",
      audienceType: "project_user",
      personaId: "pe6",
      roleId: "ro5",
      functionId: "f1",
      levelId: "lv5",
      country: "United States",
      region: "Americas",
      audienceSizeId: "as1",
      startDate: "2026-06-01",
      endDate: "2026-10-31",
      impactLevelId: "il4",
      effortLevelId: "el3",
      impactTypeIds: ["it3", "it4"],
      reactionIds: ["r3", "r7"],
      leverIds: ["l3", "l5", "l1"],
      know: "The new Workday navigation cold; the most likely top-20 questions and the right answers; the escalation tree for issues they can't resolve; the projected ticket volume and how staffing is scaled to match.",
      feel: "Confident answering even the harder questions; supported through volume spikes (no implicit expectation to absorb the surge solo); empowered to escalate without judgment.",
      doIt: "Complete the new system training before June 15; participate in the table-top simulation in late June; staff overtime shifts during the cutover week; flag emerging issue patterns to the integration team daily.",
      voiceQuote:
        "\"On cutover week I'll get yelled at by people whose paycheck is wrong, by people who can't find their PTO balance, and by managers whose new direct report isn't in the system. The system can be perfect — I still need a script for the human side.\"",
      concerns:
        "Will we have enough staff for the cutover week, or am I going to be on the phone for 14 hours a day? What do I say to someone whose paycheck is genuinely wrong — not 'I'll escalate it,' but what's actually going to happen next? Do I have authority to issue emergency comp adjustments, or do I have to escalate everything? Who covers if the call queue spikes — outside vendor? Other team? Or just us absorbing the surge?",
      successSignals:
        "Cutover-week staffing includes external surge support, not just internal overtime. They have an actual decision tree for the top-20 issue types, with real authority and clear escalation paths. The table-top simulation surfaces 5+ issues that get fixed before cutover. Their feedback is solicited daily during the cutover week and visibly acted on. They get explicit appreciation at the next all-hands by name.",
    },

    {
      id: "a15",
      changeToImplementId: "ci2",
      name: "DevOps Engineers (SoftWave)",
      impactDescription:
        "Migrate every pipeline, runner, and deploy script. This is their primary work for ~5 months. Real risk of burnout if not managed. Whether this group exits the migration energized or depleted is the single biggest predictor of whether they stay through the retention period.",
      audienceType: "end_user",
      personaId: "pe4",
      roleId: "ro4",
      functionId: "f3",
      levelId: "lv4",
      country: "United Kingdom",
      region: "Europe",
      audienceSizeId: "as1",
      startDate: "2026-04-15",
      endDate: "2026-09-30",
      impactLevelId: "il5",
      effortLevelId: "el3",
      impactTypeIds: ["it3", "it4", "it5"],
      reactionIds: ["r7", "r3"],
      leverIds: ["l5", "l1", "l3"],
      know: "The full migration scope written down (not just discussed in meetings); the realistic resourcing plan including outside help; the retention package terms tied to migration milestones; the support structure (weekly check-in, pulse survey, named escalation owner).",
      feel: "Indispensable but not exploited; trusted to do this work without micromanagement; clear that their burnout risk is being actively managed, not just noticed.",
      doIt: "Maintain the migration runbook as the source of truth; attend the weekly check-in and answer the monthly pulse honestly; raise blockers within 24h rather than absorbing them; take the rest days built into the plan rather than working through them.",
      voiceQuote:
        "\"I built the CI we're now migrating away from. I'm not bitter about it, but I'm not pretending to be excited either. I just want to do the work cleanly and then think about whether I want to be the one running the new thing.\"",
      concerns:
        "Am I migrating myself out of a job? After this work is done, is there a place for me on the unified platform team? Why isn't there outside contractor help when this is clearly 1.5x what we can deliver? What happens to my retention bonus if I leave 2 months after the migration is done — is it pro-rated or all-or-nothing? Will the post-mortem actually credit our team, or will TechLoom take the win?",
      successSignals:
        "There's a clear post-migration role offer in writing before they're 60% through the migration. They get either external contractor help or a realistic timeline extension — not the original timeline plus 'work harder.' Their team's burnout is tracked explicitly via the monthly pulse and the data is shared back. Their retention package is paid on time and in full, with no surprise conditions. They get visible credit for the migration in the all-hands when it lands.",
    },
  ],
  // Stakeholders include both named individuals (the people you brief 1:1) and
  // groups (the populations whose collective posture matters). Both go in the
  // same array; the `stakeholderType` field distinguishes them so the editor
  // can present the right UX (group cards re-label "Role" as "Description").
  stakeholders: [
    {
      id: "s1",
      stakeholderType: "individual",
      name: "Maya Chen",
      role: "CEO, TechLoom Inc.",
      impact: "high",
      support: "high",
      audienceLinkIds: ["a1"],
      engagementPlan:
        "Sponsor of the entire integration. Weekly check-ins with the integration PMO; co-owns the integration narrative externally with Priya. Visible at every major milestone all-hands. Maya is high-credibility and her words carry — every public message about the integration should be co-signed or visibly endorsed by her.",
    },
    {
      id: "s2",
      stakeholderType: "individual",
      name: "Daniel Okafor",
      role: "CTO, TechLoom Inc.",
      impact: "high",
      support: "high",
      audienceLinkIds: ["a3", "a4", "a5", "a15"],
      engagementPlan:
        "Sponsor of engineering platform consolidation. Holds the weekly cross-org engineering leadership meeting. Critical that he co-presents the migration roadmap with Liam (not solo) so engineers see joint ownership. Personally meets with every Staff+ engineer in SoftWave engineering by April 15.",
    },
    {
      id: "s3",
      stakeholderType: "individual",
      name: "Priya Raman",
      role: "President of EMEA (incoming), former SoftWave CEO",
      impact: "high",
      support: "low",
      audienceLinkIds: ["a9"],
      engagementPlan:
        "High-influence stakeholder whose public posture matters enormously. Currently a Barrier — privately conflicted about the integration. Bi-weekly executive coach engagement; bi-weekly 1:1 with Maya. Strategy is to move Priya from Barrier → Advocate through (1) visible respect for what she built, (2) real decision authority in EMEA, (3) preservation of distinctively-SoftWave practices she championed.",
    },
    {
      id: "s4",
      stakeholderType: "individual",
      name: "James Bellingham",
      role: "Chief Architect (former SoftWave CTO)",
      impact: "high",
      support: "med",
      audienceLinkIds: ["a9", "a5", "a13"],
      engagementPlan:
        "Chairs the Architecture Council with sign-off authority on unified platform decisions. Monthly executive coaching. Engagement strategy: give him real architectural authority (not ceremonial); ensure at least one major standards decision goes the SoftWave way to validate the council; pair him with Daniel for joint communications.",
    },
    {
      id: "s5",
      stakeholderType: "individual",
      name: "Sara Whitfield",
      role: "CHRO, TechLoom Inc.",
      impact: "high",
      support: "high",
      audienceLinkIds: ["a6", "a7", "a8", "a14"],
      engagementPlan:
        "Sponsor of HR systems harmonization workstream and the cultural-integration lead. Reviews monthly HRBP pulse summary. Owns the manager cascade kit quality. Champion stakeholder — her capacity is the constraint, not her support.",
    },
    {
      id: "s6",
      stakeholderType: "individual",
      name: "Liam Foster",
      role: "VP Engineering (SoftWave-side)",
      impact: "high",
      support: "low",
      audienceLinkIds: ["a3", "a4", "a15"],
      engagementPlan:
        "Currently a Barrier — privately skeptical of migration pace and worried about retention of his top engineers. Strategy is to (1) co-position him with Daniel on every migration announcement, (2) give him meaningful input on team-level migration sequencing, (3) explicitly credit his team for the migration in post-launch all-hands. Weekly 1:1 with Daniel.",
    },
    {
      id: "s7",
      stakeholderType: "individual",
      name: "Rachel Voss",
      role: "VP People Ops, TechLoom Inc.",
      impact: "med",
      support: "high",
      audienceLinkIds: ["a6", "a8"],
      engagementPlan:
        "Owns the leadership-side CRA respondent group; quarterback for the HRBP function. Bi-weekly steering committee member. Strong advocate; primary engagement is keeping her informed and unblocked.",
    },
    {
      id: "s8",
      stakeholderType: "individual",
      name: "Marcus Pinheiro",
      role: "VP Customer Success",
      impact: "med",
      support: "med",
      audienceLinkIds: ["a10"],
      engagementPlan:
        "Owns the customer communication strategy. Needs more visibility into engineering migration risk so his CSMs can pre-empt customer concerns. Monthly briefing in steering committee plus weekly access to engineering migration status.",
    },

    {
      id: "s9",
      stakeholderType: "group",
      name: "All Employees",
      role: "Combined ~2,100 employees across both organizations",
      impact: "med",
      support: "med",
      audienceLinkIds: ["a8"],
      engagementPlan:
        "Monthly all-hands cadence. Combined intranet hub. Pulse survey quarterly. Goal is informed and reassured, not enthusiastic — this group needs no surprises and timely communication, not motivation.",
    },
    {
      id: "s10",
      stakeholderType: "group",
      name: "People Managers (cross-functional)",
      role: "All managers with direct reports across both organizations (~180 managers)",
      impact: "high",
      support: "med",
      audienceLinkIds: ["a12"],
      engagementPlan:
        'Currently neutral but pivotal — the entire cascade depends on this population. Manager-only briefings two weeks ahead of every cascade. Manager-only Slack space (genuinely private). Talking points with explicit "if you don\'t know, say so" framing. HRBPs on standby during cascade weeks.',
    },
    {
      id: "s11",
      stakeholderType: "group",
      name: "Engineering Leadership Team",
      role: "Combined VP/Director-level engineering leadership across both orgs (~14 people)",
      impact: "high",
      support: "high",
      audienceLinkIds: ["a4"],
      engagementPlan:
        "Weekly engineering leadership meeting (Daniel + Liam co-chair). Architecture Council quarterly. Champion group — high alignment to date, but watch for fatigue as the migration grinds on through Q3.",
    },
    {
      id: "s12",
      stakeholderType: "group",
      name: "HR Function (combined)",
      role: "HR, People Ops, Payroll, HRBPs across both organizations (~32 people)",
      impact: "high",
      support: "high",
      audienceLinkIds: ["a6", "a7", "a14"],
      engagementPlan:
        "Weekly HR workstream stand-up. Monthly cultural-integration retrospective. Champion group; main engagement is capacity protection and clear escalation paths.",
    },
    {
      id: "s13",
      stakeholderType: "group",
      name: "Architecture Council",
      role: "Cross-org senior architects, chaired by James Bellingham (~8 people)",
      impact: "high",
      support: "med",
      audienceLinkIds: ["a5"],
      engagementPlan:
        "Bi-weekly council meeting with formal sign-off authority on platform decisions. Advocate group — supportive but watching closely whether their decisions actually stick.",
    },
    {
      id: "s14",
      stakeholderType: "group",
      name: "Top-100 Enterprise Customers",
      role: "Highest-ARR customers across both books of business",
      impact: "high",
      support: "med",
      audienceLinkIds: ["a10"],
      engagementPlan:
        "Proactive 1:1 outreach by named CSM before any externally-visible change. Quarterly executive briefing on integration progress for the top 25. Watch for any contractual concerns surfacing.",
    },
    {
      id: "s15",
      stakeholderType: "group",
      name: "Board of Directors",
      role: "TechLoom board including 2 new SoftWave-nominated members",
      impact: "high",
      support: "high",
      audienceLinkIds: [],
      engagementPlan:
        "Quarterly board update with integration scorecard. Pre-brief Maya before each board meeting on emerging integration risks. Champion group; engagement is information quality, not persuasion.",
    },
    {
      id: "s16",
      stakeholderType: "group",
      name: "Communications Team",
      role: "Internal + external comms team across both orgs (~12 people)",
      impact: "med",
      support: "high",
      audienceLinkIds: [],
      engagementPlan:
        "Weekly comms calendar review with integration PMO. Templates for manager cascade, all-hands talking points, customer outreach. Advocate group; main engagement is keeping them ahead of the news, not behind it.",
    },
  ],
  // Interventions are the specific delivery actions that operationalize each
  // change lever for each audience. cadence: 'one_time' draws as a single bar;
  // 'milestones' draws as discrete dots at each milestoneDate; 'recurring'
  // draws as a series of dots at the recurrenceInterval cadence.
  interventions: [
    {
      id: "iv1",
      name: "CEO video on integration rationale",
      description:
        "Maya records a 4-minute video framing the strategic rationale, what stays the same, what changes, and the cultural commitments TechLoom is making to the SoftWave team. Distributed simultaneously across both organizations.",
      leverId: "l1",
      audienceId: "a8",
      owner: "Communications Team (s16)",
      status: "done",
      cadence: "one_time",
      startDate: "2026-01-22",
      endDate: "2026-01-29",
    },
    {
      id: "iv2",
      name: "Hands-on platform migration training (SoftWave engineers)",
      description:
        "In-person + remote training sessions on the unified DevOps platform. Three milestone delivery dates across April, one per migration wave. Limited group size to enable Q&A.",
      leverId: "l3",
      audienceId: "a3",
      owner: "Daniel Okafor (s2)",
      status: "in_progress",
      cadence: "milestones",
      startDate: "2026-04-01",
      endDate: "2026-04-30",
      milestoneDates: ["2026-04-03", "2026-04-15", "2026-04-29"],
    },
    {
      id: "iv3",
      name: "Founder transition coaching",
      description:
        "Bi-weekly executive coaching sessions for Priya, monthly for James. External coach engaged. Confidential; outputs are coaching-only, not reported.",
      leverId: "l5",
      audienceId: "a9",
      owner: "Sara Whitfield (s5)",
      status: "in_progress",
      cadence: "recurring",
      recurrenceInterval: "biweekly",
      startDate: "2026-02-01",
      endDate: "2026-07-31",
    },
    {
      id: "iv4",
      name: "Manager cascade toolkit",
      description:
        'Talking points, FAQ, decision-tree for "what to do if asked X". Delivered to all managers two weeks before the cascade. Includes a 30-min recorded walkthrough + manager-only office hours.',
      leverId: "l3",
      audienceId: "a12",
      owner: "Rachel Voss (s7)",
      status: "done",
      cadence: "one_time",
      startDate: "2026-02-08",
      endDate: "2026-02-15",
    },
    {
      id: "iv5",
      name: "DevOps weekly check-in + monthly pulse",
      description:
        "Standing weekly 30-min check-in with the DevOps engineers + monthly anonymous pulse survey. Designed to surface burnout signals early. Daniel + Liam attend the monthly pulse review.",
      leverId: "l5",
      audienceId: "a15",
      owner: "Liam Foster (s6)",
      status: "in_progress",
      cadence: "recurring",
      recurrenceInterval: "weekly",
      startDate: "2026-04-15",
      endDate: "2026-09-30",
    },
    {
      id: "iv6",
      name: "New HR portal walkthrough + reference card",
      description:
        "15-min video walkthrough of the new self-service portal + printed reference card mailed to home addresses two weeks before cutover. Single delivery.",
      leverId: "l1",
      audienceId: "a8",
      owner: "Sara Whitfield (s5)",
      status: "planned",
      cadence: "one_time",
      startDate: "2026-06-15",
      endDate: "2026-06-30",
    },
    {
      id: "iv7",
      name: "Code review pairing pilots",
      description:
        "Pre-migration pairing pilots: pair senior engineers across both orgs to do code review on real work using the proposed unified standards. Surfaces disagreements before they become arguments.",
      leverId: "l2",
      audienceId: "a5",
      owner: "James Bellingham (s4)",
      status: "planned",
      cadence: "one_time",
      startDate: "2026-04-20",
      endDate: "2026-05-03",
    },
    {
      id: "iv8",
      name: "BU leader integration offsite",
      description:
        "2-day offsite for BU leaders from both organizations. Focus on building working relationships, charter-writing, and joint planning on cross-BU initiatives. Held in a neutral location.",
      leverId: "l5",
      audienceId: "a2",
      owner: "Maya Chen (s1)",
      status: "done",
      cadence: "one_time",
      startDate: "2026-02-10",
      endDate: "2026-02-12",
    },
    {
      id: "iv9",
      name: "Engineering manager briefing series",
      description:
        "Three milestone manager-only briefings (March, April, May) covering migration plan deep dive, Q&A with Daniel + Liam, and template conversations for tough scenarios.",
      leverId: "l3",
      audienceId: "a4",
      owner: "Daniel Okafor (s2)",
      status: "in_progress",
      cadence: "milestones",
      startDate: "2026-03-15",
      endDate: "2026-05-15",
      milestoneDates: ["2026-03-20", "2026-04-17", "2026-05-15"],
    },
    {
      id: "iv10",
      name: "Customer communication wave (enterprise)",
      description:
        "Two milestone customer communication waves. First (mid-May): proactive CSM 1:1s with top-100 enterprise customers. Second (late-June): broader email + product newsletter mention.",
      leverId: "l1",
      audienceId: "a10",
      owner: "Marcus Pinheiro (s8)",
      status: "planned",
      cadence: "milestones",
      startDate: "2026-05-15",
      endDate: "2026-06-30",
      milestoneDates: ["2026-05-18", "2026-06-22"],
    },
    {
      id: "iv11",
      name: "GDPR architecture review checkpoint",
      description:
        "Three milestone compliance checkpoints across the migration: data architecture review (April), implementation walkthrough (June), pre-launch sign-off (August). Each produces a written memo from Compliance & Legal.",
      leverId: "l3",
      audienceId: "a13",
      owner: "Compliance & Legal (a13)",
      status: "in_progress",
      cadence: "milestones",
      startDate: "2026-04-01",
      endDate: "2026-08-31",
      milestoneDates: ["2026-04-15", "2026-06-20", "2026-08-25"],
    },
    {
      id: "iv12",
      name: "HR service center surge training + table-top",
      description:
        "Two milestone deliveries: (1) full system training completion by mid-June, (2) table-top simulation of the top-20 issue types late June. Cutover-week staffing plan locked by July 1.",
      leverId: "l3",
      audienceId: "a14",
      owner: "Sara Whitfield (s5)",
      status: "planned",
      cadence: "milestones",
      startDate: "2026-06-01",
      endDate: "2026-06-26",
      milestoneDates: ["2026-06-12", "2026-06-25"],
    },
    {
      id: "iv13",
      name: "Leadership 360 + integration pulse",
      description:
        "Quarterly Leadership 360 across the combined leadership team, with integration-specific questions. Includes peer feedback across the org boundary. Maya reviews top-line findings.",
      leverId: "l6",
      audienceId: "a1",
      owner: "Maya Chen (s1)",
      status: "in_progress",
      cadence: "recurring",
      recurrenceInterval: "quarterly",
      startDate: "2026-03-01",
      endDate: "2026-12-31",
    },
    {
      id: "iv14",
      name: "Co-branded founders communication moments",
      description:
        'Four milestone joint communications throughout the year where Priya + James co-present with Maya. Designed to model "one company" at the symbol level. First: integration announcement. Last: 1-year anniversary message.',
      leverId: "l2",
      audienceId: "a9",
      owner: "Maya Chen (s1)",
      status: "in_progress",
      cadence: "milestones",
      startDate: "2026-02-01",
      endDate: "2026-12-31",
      milestoneDates: ["2026-02-05", "2026-04-22", "2026-08-12", "2026-12-15"],
    },
    {
      id: "iv15",
      name: "HRBP cross-org pairing program",
      description:
        "Each HRBP paired with a counterpart from the other organization. Monthly joint meetings, shared culture-pulse summary. Output: monthly cultural-integration report to the steering committee.",
      leverId: "l4",
      audienceId: "a6",
      owner: "Rachel Voss (s7)",
      status: "in_progress",
      cadence: "recurring",
      recurrenceInterval: "monthly",
      startDate: "2026-02-15",
      endDate: "2026-08-15",
    },
  ],
  // Change Readiness Assessments — 8-category scoring. Numeric keys (1-8) on
  // both responses and comments map to craCategories.num.
  assessments: [
    {
      id: "asmt1",
      name: "Engineering org readiness — pre-migration (SoftWave side)",
      respondentName: "Liam Foster",
      respondentRole: "VP Engineering (SoftWave)",
      createdAt: "2026-03-15",
      responses: {
        1: 3, // Vision
        2: 4, // Case for Change
        3: 2, // Strategy
        4: 3, // Infrastructure
        5: 2, // Personal Motivation
        6: 2, // Communication
        7: 4, // Sponsorship
        8: 3, // Capability
      },
      comments: {
        1: "People know we're integrating. They don't yet know what 'integrated' means for day-to-day engineering work. Need to translate 'unified DevOps platform' from strategy-speak into concrete pictures of what code review, deploys, and on-call will look like.",
        2: "The acquisition rationale was clear and well-received at the all-hands. Engineers understand the business case. The 'why now' is solid.",
        3: "The migration plan exists at a high level but the per-team transition path is missing. Engineers can't yet answer 'when does my team move and what changes the day after?' That gap is creating background anxiety.",
        4: "TechLoom platform is solid. SoftWave engineers will need new accounts, training environments, and access provisioning — those workstreams are in flight but not yet tested at scale.",
        5: "Mixed picture. Senior engineers who joined SoftWave for the autonomy are most resistant. Mid-level engineers see opportunity for broader exposure. Juniors are largely indifferent.",
        6: "Communication is the weakest spot. Three months in, the only formal channel has been the monthly all-hands. Engineers want a dedicated Slack channel, a written FAQ, and predictable cadence — none of which exist yet.",
        7: "Daniel (TechLoom CTO) has been visible and supportive. James (former SoftWave CTO) is engaged but his future role is still ambiguous, which dampens the signal he can send.",
        8: "Capability is in reasonable shape. Most engineers have used similar tools elsewhere. Pockets of risk: the platform-engineering team who built SoftWave's bespoke CI will need to be reabsorbed rather than retrained.",
      },
      notes:
        "Sponsorship and case for change are well-understood (categories 2 and 7). Communication and personal motivation are the soft spots (categories 5 and 6, both scoring 2) — engineers know change is coming but don't yet see a credible plan for the migration itself, and the most senior engineers are the most worried about what they lose.\n\nTop 3 priorities for the next 30 days:\n\n1. **Translate strategy into team-level transition plans** (addresses category 3, score 2). Today engineers can't answer 'when does my team move and what changes the day after?' Action: Daniel + Liam co-author a per-team migration calendar by April 1. Each team gets named dates, named owners, named blockers.\n\n2. **Increase communication cadence + add a written reference layer** (addresses category 6, score 2). Action: launch the engineering-only Slack channel by March 25; publish the migration FAQ by April 1; commit to a bi-weekly engineering all-hands for the duration of the migration.\n\n3. **Address senior-engineer-specific concerns directly** (addresses category 5, score 2). Action: Daniel personally meets with every Staff+ engineer in SoftWave engineering by April 15 (~20 conversations). Listening-first agenda. Findings inform the migration playbook.\n\nThe 60-day pulse should specifically re-test categories 3, 5, and 6 to verify movement.",
    },
    {
      id: "asmt2",
      name: "Leadership team readiness — first-90-days check",
      respondentName: "Rachel Voss",
      respondentRole: "VP People Ops (TechLoom)",
      createdAt: "2026-04-10",
      responses: {
        1: 4, // Vision
        2: 5, // Case for Change
        3: 4, // Strategy
        4: 4, // Infrastructure
        5: 4, // Personal Motivation
        6: 3, // Communication
        7: 5, // Sponsorship
        8: 4, // Capability
      },
      comments: {
        1: "Leadership team is aligned on the vision after the January offsite. Each leader can articulate the integrated company's strategic priorities in their own words.",
        2: "Strongest category. The case for the acquisition is well-understood and well-defended at this level. No revisiting required.",
        3: "Plan is solid at the workstream level. The seam between workstreams (e.g. when engineering migration meets HR systems harmonization for support tickets) is where the planning is thinnest.",
        4: "Resources are adequately allocated. Watch HR ops capacity in June-July when engineering migration cascade peaks alongside HRIS cutover prep.",
        5: "Personal motivation is high in the leadership team — this is a once-in-a-career integration and the team wants to do it well. Risk is fatigue by Q3, not engagement.",
        6: "Leadership thinks they're communicating clearly. The engineering-side CRA disagrees. This is the key gap to close — the perception of communication quality differs sharply across levels.",
        7: "Maya's sponsorship is exemplary. Sara is fully bought in. Daniel is the most-needed and the most-visible sponsor. No sponsorship gap at this level.",
        8: "Leadership team has the right experience for an integration of this size and complexity. Several have led similar acquisitions previously.",
      },
      notes:
        "Leadership view is meaningfully more optimistic than engineering view (asmt1) — that's the gap to close. Most striking divergence: communication scored 3 from leadership vs 2 from engineering. We THINK we're communicating well; the people on the receiving end disagree.\n\nTop 3 priorities for the next 30 days:\n\n1. **Close the perception gap on communication** (asmt2 cat 6 = 3; asmt1 cat 6 = 2). Action: launch a monthly 'reverse-cascade' practice where engineer feedback flows up through HRBPs to the leadership team, summarized and reviewed. Track gap closure in next pulse.\n\n2. **Translate leadership confidence into manager equipment** (asmt2 cat 8 = 4, but the gap is at manager level). Action: invest in the manager cascade kit (iv4) seriously — it's already shipped but quality is variable. Add scenario-based role-play to the next manager forum.\n\n3. **Maintain the strengths**. Sponsorship (cat 7 = 5) and case for change (cat 2 = 5) are where they should be. Don't let leadership take their foot off the gas on these — they feel done, but the engineering pulse shows the message hasn't fully landed.\n\nKey watch: re-run both CRAs in 60 days. We want the engineering CRA to come up on cats 3, 5, 6 (currently the soft spots). We want the leadership CRA to NOT come down on cats 2, 7, 8.",
    },
  ],
  // Sizing & Impact assessments — 13 questions across two dimensions. Numeric
  // keys (1-13) on both responses and comments map to sizingQuestions.num.
  sizingAssessments: [
    {
      id: "siz1",
      name: "Engineering Platform Consolidation — sizing",
      respondentName: "CK",
      respondentRole: "Lead consultant assessment",
      createdAt: "2026-03-12",
      responses: {
        1: 3, // # impacted
        2: 4, // magnitude
        3: 3, // concurrent changes
        4: 2, // reaction (reverse-scored)
        5: 3, // HR involvement
        6: 4, // corporate involvement
        7: 5, // BU involvement
        8: 3, // HR resources
        9: 4, // corporate resources
        10: 5, // BU resources
        11: 3, // change leadership
        12: 4, // employee impact
        13: 3, // leader impact
      },
      comments: {
        1: "340 SoftWave engineers directly affected. Adjacent engineering populations indirectly affected — total touched ~600.",
        2: "Multiple impacts across one primary audience (engineering). Tooling change + ways-of-working change + cultural change stacked.",
        3: "Engineering is also absorbing the HR systems harmonization and ongoing product roadmap pressure simultaneously.",
        4: "Reactions skew negative — particularly among senior engineers. Reverse-scored.",
        5: "Sara Whitfield needs to be actively involved in cultural-integration aspects, less so on the technical side.",
        6: "Maya + Daniel are continuously involved. Weekly review cadence.",
        7: "Engineering BU leadership (Daniel, Liam) carry the workstream end-to-end.",
        8: "HRBPs and culture-side resources needed, not large in number.",
        9: "Significant corporate resources — change management, comms, IT, security all involved.",
        10: "Heavy engineering resource commitment from Liam's team for migration runbooks and Daniel's team for platform readiness.",
        11: "Daniel is experienced with change. Liam less so. Combined effectiveness is medium-strong.",
        12: "Large impact on every engineer's daily workflow for ~6 months.",
        13: "Liam's role in particular changes meaningfully; Daniel's scope grows.",
      },
      notes:
        "Tier 3 deliverables required given scale (340 affected engineers) and complexity (active production systems, multiple concurrent changes for the same population). Recommendation: full CM workstream with dedicated change manager, formal CRA cadence, sponsor coalition, and structured intervention plan.\n\nKey rationale points:\n• Sizing dimension scores high on magnitude (multiple impact types layered on one population) and concurrency (engineering is absorbing 3 workstreams simultaneously).\n• Impact dimension scores high on resource intensity (corporate + BU resources both heavy) and on employee impact (6-month sustained disruption to daily workflow).\n• Reaction dimension is the headline risk: senior engineers are the most resistant population AND the most influential. Treatment needs to be intervention-rich (multiple touchpoints, listening loops, joint visibility from Daniel+Liam) rather than communication-heavy.",
    },
    {
      id: "siz2",
      name: "HR Systems Harmonization — sizing",
      respondentName: "MR",
      respondentRole: "HR transformation lead",
      createdAt: "2026-04-05",
      responses: {
        1: 4, // # impacted
        2: 3, // magnitude
        3: 2, // concurrent changes
        4: 4, // reaction (reverse-scored — positive reaction)
        5: 5, // HR involvement
        6: 2, // corporate involvement
        7: 2, // BU involvement
        8: 4, // HR resources
        9: 2, // corporate resources
        10: 1, // BU resources
        11: 4, // change leadership
        12: 2, // employee impact
        13: 2, // leader impact
      },
      comments: {
        1: "All 2,100 employees affected, but the actual behavior change is small per person.",
        2: "Single primary impact type (tooling) across all employees. Magnitude is breadth, not depth.",
        3: "Affected population not absorbing as much other change (the engineering team is, but that's already in siz1).",
        4: "Reaction is mildly positive — employees expect modern HR systems. Reverse-scored, so this contributes positively.",
        5: "Extremely HR-led. Sara's team owns end-to-end.",
        6: "Corporate leadership involvement is light — sign-off and oversight only.",
        7: "BU leadership involvement is minimal except for communication cascade support.",
        8: "Heavy HR resource commitment, primarily payroll ops and HRIS team.",
        9: "Light corporate resourcing — mostly IT security review and comms support.",
        10: "Almost no BU resourcing required.",
        11: "Sara is experienced; HR team has done a Workday cutover before.",
        12: "Modest employee impact — verify your record, re-elect benefits, learn new portal.",
        13: "Small leader impact — manager-side workflow is similar to today.",
      },
      notes:
        "Tier 2 deliverables sufficient. Major effort is technical (HR ops side), not behavioral (end user side). Standard comms + clear self-service guides should carry it. Watch the HR service center for ticket spikes during cutover.\n\nWhat this means in practice (vs Engineering Platform sizing):\n\n• **Lighter overall CM footprint.** Senior associate-led, not lead consultant-led. Reuse the engineering workstream's comms infrastructure (intranet hub, manager toolkit format) rather than building new from scratch.\n\n• **Concentrated effort at cutover, not across the whole arc.** Engineering migration is a 6-month sustained CM workstream. HR is a 1-week intense burst around cutover, then taper.\n\n• **Different reaction pattern.** Engineering has resistance from senior identity-driven engineers. HR has logistical anxiety (will my paycheck be right?). Different intervention shapes: engineering needs co-ownership + listening; HR needs reassurance + clear self-service + a hotline.\n\n• **Cross-staffing risk.** Same HR ops team that's running this workstream is supporting the engineering migration cascade. Watch capacity in June-July — that's when both stack.",
    },
  ],
  // AI-extracted proposals awaiting consultant review.
  // Each: { id, kind: 'change'|'audience'|'stakeholder'|'intervention',
  //         status: 'pending'|'rejected'|'applied'|'merged',
  //         source: filename, sourceQuote: '...short excerpt from doc...',
  //         data: {…shape depends on kind}, dupOf?: existingId,
  //         extractedAt: ISO }
  // Mix of pending/applied/rejected, two dup-of cases to demonstrate merge UX.
  pendingProposals: [
    // --- PENDING: from the integration charter extraction ---
    {
      id: "prop1",
      kind: "change",
      status: "pending",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Stand up unified customer success organization across both books of business within 9 months",
      data: {
        name: "Customer Success Integration",
        description:
          "Stand up unified customer success org across both books of business. Includes CSM territory remapping, NPS measurement alignment, and shared QBR cadences.",
        isKey: true,
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },
    {
      id: "prop2",
      kind: "audience",
      status: "pending",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Account executives across both regions will need to absorb new product lines and cross-sell motions",
      data: {
        name: "Account Executives (Both Regions)",
        impactDescription:
          "Will absorb new product lines into their portfolio and learn cross-sell motions across the combined offering. Compensation plan changes expected at start of FY26.",
        suggestedChange: "Customer Success Integration",
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },
    {
      id: "prop3",
      kind: "stakeholder",
      status: "pending",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Anika Devi joins as VP Customer Success leading the integration of both org's CS functions",
      data: {
        name: "Anika Devi",
        role: "VP Customer Success (incoming)",
        abilityToImpact: "high",
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },
    {
      id: "prop4",
      kind: "intervention",
      status: "pending",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Run joint CSM listening sessions to surface client-experience risks before territory remapping is finalized",
      data: {
        name: "Joint CSM listening sessions",
        description:
          "Pre-remapping listening tour where CSMs from both orgs share territory-level intelligence and surface client-risk hot spots.",
        suggestedAudience: "",
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },

    // --- PENDING with DUPLICATE flag — will show "Merge into existing" UX ---
    {
      id: "prop5",
      kind: "stakeholder",
      status: "pending",
      source: "SoftWave-Founders-Bio.pdf",
      sourceFileId: "cc2",
      sourceQuote:
        "Priya Raman, co-founder and former CEO, will join the TechLoom executive team as President of EMEA",
      data: {
        name: "Priya Raman",
        role: "President of EMEA (incoming, former SoftWave CEO)",
        abilityToImpact: "high",
      },
      dupOf: "s3", // matches existing stakeholder — will show merge option
      extractedAt: "2026-03-19T09:14:00Z",
    },

    // --- APPLIED (already accepted, shows in "Show accepted/rejected" filter) ---
    {
      id: "prop6",
      kind: "change",
      status: "applied",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Engineering platform consolidation is the largest workstream with highest risk to operations",
      data: {
        name: "Engineering Platform Consolidation",
        description:
          "Migrate SoftWave engineering teams from legacy stack onto TechLoom unified DevOps platform.",
        isKey: true,
      },
      dupOf: null,
      extractedAt: "2026-03-15T11:02:00Z",
    },

    // --- REJECTED (consultant decided no, kept in history to avoid re-proposing) ---
    {
      id: "prop7",
      kind: "audience",
      status: "rejected",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Office facilities and IT support will need updated badge systems across both organizations",
      data: {
        name: "Facilities & IT Support Staff",
        impactDescription: "Badge system updates across both organizations.",
        suggestedChange: "",
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },

    // --- More proposals from the Founders Bio PDF ---
    {
      id: "prop8",
      kind: "intervention",
      status: "pending",
      source: "SoftWave-Founders-Bio.pdf",
      sourceFileId: "cc2",
      sourceQuote:
        "Preserve SoftWave's Friday demo culture (weekly engineering demos open to the whole company) post-integration",
      data: {
        name: "Preserve Friday demo culture cross-org",
        description:
          "Extend SoftWave's open weekly demo practice to combined engineering org. Symbolic preservation of SoftWave identity; practical knowledge transfer between teams. Owner: James Bellingham.",
        suggestedAudience: "SoftWave Engineering Staff",
      },
      dupOf: null,
      extractedAt: "2026-03-19T09:14:00Z",
    },
    {
      id: "prop9",
      kind: "stakeholder",
      status: "pending",
      source: "SoftWave-Founders-Bio.pdf",
      sourceFileId: "cc2",
      sourceQuote:
        "James Bellingham chairs the Architecture Council with sign-off authority on unified platform decisions",
      data: {
        name: "James Bellingham",
        role: "Chief Architect (former SoftWave CTO)",
        abilityToImpact: "high",
      },
      dupOf: "s4", // matches existing — will show merge option
      extractedAt: "2026-03-19T09:14:00Z",
    },
    {
      id: "prop10",
      kind: "audience",
      status: "pending",
      source: "SoftWave-Founders-Bio.pdf",
      sourceFileId: "cc2",
      sourceQuote:
        "No mandatory return-to-office change during the first 18 months for SoftWave staff",
      data: {
        name: "SoftWave Remote-First Employees",
        impactDescription:
          "SoftWave operated remote-first; the integration commitment is to preserve this for 18 months. Risk: TechLoom culturally favors hybrid presence — this difference may quietly create a two-tier feel during the transition window.",
        suggestedChange: "Leadership Reorganization",
      },
      dupOf: null,
      extractedAt: "2026-03-19T09:14:00Z",
    },

    // --- From Q1 Board Update ---
    {
      id: "prop11",
      kind: "change",
      status: "pending",
      source: "Q1-Board-Update.docx",
      sourceFileId: "cc3",
      sourceQuote:
        "31% of SoftWave engineers said they were concerned about losing what makes the team special",
      data: {
        name: "Cultural Preservation Program",
        description:
          "Targeted workstream to identify, document, and protect distinctively-SoftWave practices that should survive integration. Triggered by Q1 pulse showing 31% SoftWave-engineer concern. Recommend dedicated cultural-integration lead reporting to Sara Whitfield.",
        isKey: false,
      },
      dupOf: null,
      extractedAt: "2026-04-02T08:30:00Z",
    },
    {
      id: "prop12",
      kind: "intervention",
      status: "pending",
      source: "Q1-Board-Update.docx",
      sourceFileId: "cc3",
      sourceQuote:
        "Recommend more visible co-ownership of the migration plan from Daniel and Liam together",
      data: {
        name: "Daniel + Liam joint migration office hours",
        description:
          "Weekly 45-min open office hours co-hosted by Daniel Okafor and Liam Foster, visible to all engineering staff. Purpose: model joint ownership of the migration; surface concerns in a forum where both leaders respond together rather than separately.",
        suggestedAudience: "SoftWave Engineering Staff",
      },
      dupOf: null,
      extractedAt: "2026-04-02T08:30:00Z",
    },
    {
      id: "prop13",
      kind: "intervention",
      status: "pending",
      source: "Q1-Board-Update.docx",
      sourceFileId: "cc3",
      sourceQuote:
        "a written reference layer that engineers can consult outside of meetings",
      data: {
        name: "Engineering migration written reference layer",
        description:
          "Comprehensive written reference (FAQ, decision log, glossary of new tooling, team-level migration calendar) so engineers can self-serve answers between meetings. Reduces meeting load on Liam and Daniel; reduces anxiety from 'I have to wait until Friday to find out.'",
        suggestedAudience: "SoftWave Engineering Staff",
      },
      dupOf: null,
      extractedAt: "2026-04-02T08:30:00Z",
    },

    // --- Already APPLIED items so the "history" tab has content ---
    {
      id: "prop14",
      kind: "audience",
      status: "applied",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "SoftWave engineering staff migrate from legacy GitLab + CircleCI stack to TechLoom GitHub + internal CI",
      data: {
        name: "SoftWave Engineering Staff",
        impactDescription: "Migrate from legacy stack to unified DevOps.",
        suggestedChange: "Engineering Platform Consolidation",
      },
      dupOf: null,
      extractedAt: "2026-03-15T11:02:00Z",
    },
    {
      id: "prop15",
      kind: "stakeholder",
      status: "applied",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Sara Whitfield (CHRO) sponsors the HR systems harmonization workstream",
      data: {
        name: "Sara Whitfield",
        role: "CHRO, TechLoom Inc.",
        abilityToImpact: "high",
      },
      dupOf: null,
      extractedAt: "2026-03-15T11:08:00Z",
    },

    // --- More REJECTED items showing AI extraction can over-propose ---
    {
      id: "prop16",
      kind: "intervention",
      status: "rejected",
      source: "TechLoom-Integration-Charter.pptx",
      sourceFileId: "cc1",
      sourceQuote:
        "Consider quarterly executive yoga retreats to build cross-org rapport",
      data: {
        name: "Quarterly executive yoga retreats",
        description:
          "Quarterly off-site for senior leadership focused on wellness and informal cross-org connection.",
        suggestedAudience: "Executives and Senior Leadership",
      },
      dupOf: null,
      extractedAt: "2026-03-18T14:22:00Z",
    },
    {
      id: "prop17",
      kind: "change",
      status: "rejected",
      source: "Q1-Board-Update.docx",
      sourceFileId: "cc3",
      sourceQuote: "One executive departure (anticipated) since announcement",
      data: {
        name: "Executive Departure Backfill",
        description:
          "Plan and execute backfill for the executive who departed during Q1.",
        isKey: false,
      },
      dupOf: null,
      extractedAt: "2026-04-02T08:30:00Z",
    },
  ],
  // Terminology overrides — TechLoom's voice. When the "Client terms" toggle
  // is on, these labels replace the WTW canonical taxonomy labels throughout
  // the UI. Tech-forward language: "Loomers" for employees, "Engineering" for
  // IT, flatter level naming, "Tell/Sell/Equip/Bake In" for change levers.
  terminologyOverrides: {
    roles: {
      ro1: "Leadership",
      ro2: "Team Lead",
      ro3: "Senior IC",
      ro4: "Platform Engineer",
      ro5: "Operations Partner",
      ro7: "All Loomers",
    },
    functions: {
      f1: "People Team",
      f2: "Finance & Strategy",
      f3: "Engineering",
      f4: "Go-To-Market",
      f5: "Operations",
      f6: "Customer Success",
      f7: "Legal & Compliance",
      f8: "Cross-functional",
    },
    levels: {
      lv1: "C-Level",
      lv2: "Director+",
      lv3: "Staff/Principal",
      lv4: "Senior",
      lv5: "Mid-level",
      lv6: "Junior",
      lv7: "Associate",
      lv8: "All Loomers",
    },
    workstreams: {
      ws1: "People Workstream",
      ws3: "Platform Workstream",
      ws4: "External Comms Workstream",
      ws5: "Tech Ops Workstream",
    },
    impactLevels: {
      il4: "Significant disruption",
      il5: "Foundational shift",
    },
    effortLevels: {
      el3: "Heavy lift",
    },
    reactionTypes: {
      r1: "Identity attachment",
      r2: "Uncertainty about the path",
      r3: "Cognitive overload",
      r4: "Loss of autonomy",
      r5: "Worry about competence",
      r6: "Mixed feelings",
      r7: "Frustration",
      r8: "Genuine excitement",
    },
    impactTypes: {
      it1: "Org Structure",
      it2: "Process & Policy",
      it3: "Tooling",
      it4: "Skills",
      it5: "Ways of Working",
      it6: "Culture & Norms",
    },
    changeLevers: {
      l1: "Tell",
      l2: "Sell",
      l3: "Equip",
      l4: "Bake In",
      l5: "Hear",
      l6: "Measure",
    },
  },
};
